import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import { toast } from "sonner";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, PencilIcon, DownloadSimpleIcon, PrinterIcon, ReceiptIcon, LockIcon, CheckCircleIcon, ClockCounterClockwiseIcon, MagnifyingGlassIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";
import { printDocument, printSignedDocument } from "@/lib/print";
import { fmtDateTimeIST } from "@/lib/datetime";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, DocDownloadIcons } from "@/components/DocDownloadActions";
import { RowActionsMenu } from "@/components/RowActionsMenu";
// Feb 2026 · Central UoM/currency formatters — MTR whole+Indian, KG 3 dp
// Indian, INR 2 dp Indian, USD 4 dp US. Retrofits PL / DC / Tax-Invoice
// prints so on-screen dashboards and the printed PDF speak the same
// number language.
import { fmtQty, fmtInr } from "@/lib/uom";
import { useAuth } from "@/lib/auth";
import { useRbac } from "@/lib/rbac";

import FormattedNumberInput from "@/components/FormattedNumberInput";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm";

export function PackingListsTab({ onNextStep } = {}) {
  const { hasPerm } = useAuth();
  // Aug 31, 2026 · Reactive perm gate via `useRbac()` — re-renders as
  // soon as `/rbac/me/permissions` resolves post-login. `ready` keeps
  // Create buttons DISABLED (not hidden) during the initial fetch.
  const { can: rbacCan, ready: rbacReady } = useRbac();
  const canCreatePl = rbacCan("sales_orders_packing", "create") || rbacCan("sales_orders_packing", "edit") || rbacCan("dispatch", "edit");
  // Aug 31, 2026 · Reactive Delete gate — parity with canCreatePl
  const canDeletePl = rbacCan("sales_orders_packing", "delete") || rbacCan("dispatch", "delete") || rbacCan("dispatch", "edit");
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dcs, setDcs] = useState([]);
  const [slips, setSlips] = useState([]);
  // Feb 2026 · Product Master (`items`) loaded so HS Codes can be auto-pulled
  // per line-item on the PL — mirrors the Export Packing List behaviour.
  const [itemsMaster, setItemsMaster] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | dispatched
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc | date_asc | pl_asc | pl_desc | boxes_desc | qty_desc
  const [search, setSearch] = useState("");
  // "Pull from Packing Slips" mode (per-user Feb-2026 request): the dialog can now
  // be authored either as manual item-wise entries OR by cherry-picking existing
  // Packing Slips for the selected SO. `sourceMode` toggles the UI; the two
  // pickers write back to the same underlying form / payload so downstream
  // (Delivery Challan, prints, totals) don't care which route was used.
  const [sourceMode, setSourceMode] = useState("manual"); // "manual" | "slips"
  // Mar 2, 2026 · Dispatch Source toggle — Path A (Packing Floor) vs
  // Path B (FG-STORE). Auto-stamps `source_godown_code` on save.
  const [dispatchSource, setDispatchSource] = useState("PACKING-FLOOR");
  const [selectedSlipIds, setSelectedSlipIds] = useState(new Set());
  const [productCodeFilter, setProductCodeFilter] = useState(new Set());
  const empty = {
    pl_no: "", order_id: "", pack_date: new Date().toISOString().slice(0, 10),
    customer_name: "", customer_po_no: "",
    no_of_packages: 0, gross_weight: 0, net_weight: 0, quantity: 0, remarks: "",
    entries: [],
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    // Sep 03, 2026 · 403-tolerant Promise.all — a Packing role that owns
    // `sales_orders_packing:view` but not `sales_orders:view` no longer
    // triggers the runtime overlay when it lands on this tab.
    const [p, o, c, d, s, im] = await Promise.all([
      api.get("/packing-lists/").catch(() => ({ data: [] })),
      api.get("/sales-orders/").catch(() => ({ data: [] })),
      api.get("/customers/").catch(() => ({ data: [] })),
      api.get("/delivery-challans/").catch(() => ({ data: [] })),
      api.get("/packing-slips/").catch(() => ({ data: [] })),
      api.get("/items/").catch(() => ({ data: [] })),
    ]);
    setRows(p.data); setOrders(dedupById(o.data)); setCustomers(c.data); setDcs(d.data); setSlips(s.data); setItemsMaster(im.data || []);
  };
  // Mar 2026 · HS-code resolver — matches Product Master by every field
  // an operator might pass in: `product_code` (canonical, post-Feb 2026
  // rename), `description` (legacy PLs), `code`, or `article_no`. Also
  // does a case-insensitive compare so a whitespace slip doesn't break
  // the lookup and print/View both stayed blank.
  const hsCodeFor = (articleNo) => {
    if (!articleNo) return "";
    const needle = String(articleNo).trim().toLowerCase();
    const m = (itemsMaster || []).find(x =>
      (x.product_code || "").toLowerCase() === needle ||
      (x.description || "").toLowerCase()  === needle ||
      (x.code || "").toLowerCase()         === needle ||
      (x.article_no || "").toLowerCase()   === needle
    );
    return (m && (m.hs_code || m.hsn_code)) || "";
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Pick up a one-shot article filter handed off by the Orders tab (via article-chip click).
  useEffect(() => {
    try {
      const v = sessionStorage.getItem("mes_pl_article_filter");
      if (v) { setSearch(v); sessionStorage.removeItem("mes_pl_article_filter"); }
    } catch (e) { console.warn("sessionStorage unavailable for article filter", e); }
  }, []);

  // Next-step handoff — if the previous tab queued a SO for us, auto-open the
  // New Packing List dialog with it prefilled once orders/rows are loaded.
  useEffect(() => {
    if (!orders.length) return;
    let orderId = "";
    try { orderId = sessionStorage.getItem("mes_pl_next_order") || ""; } catch (e) { /* noop */ }
    if (!orderId) return;
    try { sessionStorage.removeItem("mes_pl_next_order"); } catch (e) { /* noop */ }
    (async () => {
      await openNew();
      // openNew already fired setForm — re-invoke onOrderChange to pull line items.
      onOrderChange(orderId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const customerName = (id) => customers.find(x => x.id === id)?.name || "";

  // Map: pl_id -> dc_no it's linked to (for the status column)
  const plToDc = useMemo(() => {
    const m = {};
    dcs.forEach(d => {
      const ids = d.packing_list_ids && d.packing_list_ids.length ? d.packing_list_ids : (d.packing_list_id ? [d.packing_list_id] : []);
      ids.forEach(id => { if (id) m[id] = d.dc_no; });
    });
    return m;
  }, [dcs]);

  // Sum of qty already packed per item_no across other PLs for a given SO (excludes current editing PL)
  const packedByItem = (order_id, excludePlId = null) => {
    const map = {};
    rows.filter(r => r.order_id === order_id && r.id !== excludePlId).forEach(r => {
      (r.entries || []).forEach(e => {
        const key = e.item_no || "";
        const q = (Number(e.no_of_boxes) || 0) * (Number(e.meter_per_box) || 0);
        map[key] = (map[key] || 0) + q;
      });
    });
    return map;
  };

  // Total boxes already used across other PLs of this SO — used to continue box numbering
  const boxesUsed = (order_id, excludePlId = null) =>
    rows.filter(r => r.order_id === order_id && r.id !== excludePlId)
        .reduce((a, r) => a + (Number(r.no_of_packages) || 0), 0);

  // Total pending across all items of a given order (order_qty+extra − already packed by other PLs).
  const soPending = useMemo(() => {
    const m = {};
    orders.forEach(o => {
      const packed = {};
      rows.filter(r => r.order_id === o.id).forEach(r => (r.entries || []).forEach(e => {
        const k = String(e.item_no || "");
        packed[k] = (packed[k] || 0) + (Number(e.no_of_boxes) || 0) * (Number(e.meter_per_box) || 0);
      }));
      let total = 0;
      (o.items || []).forEach(li => {
        const k = String(li.item_no || "");
        const oq = (Number(li.order_qty) || 0) + (Number(li.extra_qty) || 0);
        total += Math.max(0, oq - (packed[k] || 0));
      });
      m[o.id] = total;
    });
    return m;
  }, [orders, rows]);

  const openNew = () => {
    setEditing(null);
    // Feb 2026 · PL No is auto-assigned on save (mirrors SO No behaviour).
    // Reserving at open leaked numbers when the operator cancelled — we now
    // reserve just-in-time from /next/packing-list-no inside `save()` so
    // sequences stay dense.
    setForm({ ...empty, pl_no: "" });
    setSourceMode("manual");
    setDispatchSource("PACKING-FLOOR");
    setSelectedSlipIds(new Set());
    setProductCodeFilter(new Set());
    setOpen(true);
  };
  const openEdit = (r) => {
    if (plToDc[r.id]) {
      toast.error(`${r.pl_no} is dispatched via ${plToDc[r.id]} — cannot be edited`);
      return;
    }
    setEditing(r);
    // rebuild entries with fresh pending calc based on other PLs
    const o = orders.find(x => x.id === r.order_id);
    const packed = packedByItem(r.order_id, r.id);
    const existing = r.entries || [];
    const entries = (o?.items || []).map((li, i) => {
      const item_no = li.item_no || String(i + 1).padStart(3, "0");
      const orderQty = (Number(li.order_qty) || 0) + (Number(li.extra_qty) || 0);
      const found = existing.find(e => (e.item_no || "") === item_no) || {};
      return {
        item_no,
        article_no: li.article_no || "",
        description: li.description || "",
        colour: li.colour || "",
        // Feb 2026 · pull HS Code from Product Master (fallback to any
        // hs_code snapshot already saved on the entry).
        hs_code: found.hs_code || hsCodeFor(li.article_no) || "",
        order_qty: orderQty,
        already_packed: packed[item_no] || 0,
        no_of_boxes: found.no_of_boxes || 0,
        meter_per_box: found.meter_per_box || 0,
        net_wt_per_box: found.net_wt_per_box || 0,
        tare_wt_per_box: found.tare_wt_per_box || 0,
      };
    });
    setForm({ ...empty, ...r, entries });
    setSourceMode(r.source_mode || "manual");
    setDispatchSource((r.dispatch_source || "PACKING-FLOOR").toUpperCase());
    setSelectedSlipIds(new Set(r.packing_slip_ids || []));
    setProductCodeFilter(new Set());
    setOpen(true);
  };

  const onOrderChange = (order_id) => {
    const o = orders.find(x => x.id === order_id);
    const pending = soPending[order_id] ?? 0;
    if (pending <= 0) {
      toast.error(`${o?.order_no} is fully packed — no pending quantity`);
      return;
    }
    const packed = packedByItem(order_id, editing?.id || null);
    const entries = (o?.items || []).map((li, i) => {
      const item_no = li.item_no || String(i + 1).padStart(3, "0");
      return {
        item_no,
        article_no: li.article_no || "",
        description: li.description || "",
        colour: li.colour || "",
        hs_code: hsCodeFor(li.article_no) || "",
        order_qty: (Number(li.order_qty) || 0) + (Number(li.extra_qty) || 0),
        already_packed: packed[item_no] || 0,
        no_of_boxes: 0,
        meter_per_box: 0,
        net_wt_per_box: 0,
        tare_wt_per_box: 0,
      };
    });
    // Derive customer PO: prefer order-level po_number, else first item's customer_po_no
    const cpo = o?.po_number || (o?.items || []).map(x => x.customer_po_no).find(v => v) || "";
    const cname = customerName(o?.customer_id);
    setForm(f => ({ ...f, order_id, entries, customer_name: cname, customer_po_no: cpo }));
  };

  const boxStartIdx = form.order_id ? boxesUsed(form.order_id, editing?.id || null) + 1 : 1;

  const updateEntry = (i, k, v) => {
    const arr = [...form.entries];
    arr[i][k] = v;
    setForm({ ...form, entries: arr });
  };

  // aggregate totals from entries
  const totals = form.entries.reduce((a, e) => {
    const b = Number(e.no_of_boxes) || 0;
    const mpb = Number(e.meter_per_box) || 0;
    const npb = Number(e.net_wt_per_box) || 0;
    const tpb = Number(e.tare_wt_per_box) || 0;
    a.boxes += b;
    a.qty += b * mpb;
    a.net += b * npb;
    a.tare += b * tpb;
    a.gross += b * (npb + tpb);
    return a;
  }, { boxes: 0, qty: 0, net: 0, tare: 0, gross: 0 });

  // Generate expanded box-wise list. startIdx allows continuing box numbering across PLs.
  const generateBoxes = (entries, startIdx = 1) => {
    const boxes = [];
    let idx = startIdx;
    (entries || []).forEach(e => {
      const b = parseInt(e.no_of_boxes) || 0;
      for (let i = 0; i < b; i++) {
        boxes.push({
          box_no: `BOX-${String(idx).padStart(4, "0")}`,
          slip_no: "",
          slip_box_no: "",
          article_no: e.article_no,
          item_no: e.item_no,
          hs_code: e.hs_code || "",
          meter: Number(e.meter_per_box) || 0,
          joints: 0,
          net_wt: Number(e.net_wt_per_box) || 0,
          tare_wt: Number(e.tare_wt_per_box) || 0,
          gross_wt: (Number(e.net_wt_per_box) || 0) + (Number(e.tare_wt_per_box) || 0),
        });
        idx++;
      }
    });
    return boxes;
  };

  // -------- Slips-mode helpers --------
  // Map: slip_id -> pl_no of the PL that already consumed it (excludes the PL being edited).
  const slipUsedInPl = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (editing && r.id === editing.id) return;
      (r.packing_slip_ids || []).forEach(sid => { if (sid) m[sid] = r.pl_no; });
    });
    return m;
  }, [rows, editing]);

  // All distinct product codes for the current SO — union of SO items' article_no
  // and product_codes found on any packing slip attached to this SO.
  const soProductCodes = useMemo(() => {
    if (!form.order_id) return [];
    const o = orders.find(x => x.id === form.order_id);
    const set = new Set();
    (o?.items || []).forEach(li => { if (li.article_no) set.add(li.article_no); });
    slips.filter(s => s.order_id === form.order_id).forEach(s => {
      (s.boxes || []).forEach(b => { if (b.product_code) set.add(b.product_code); });
    });
    return Array.from(set).sort();
  }, [orders, slips, form.order_id]);

  // Feb 28, 2026 · Pack-First lock — an excess slip is blocked ONLY when
  // its linked excess_production entry isn't yet 'Approved for Billing'.
  const [excessEntries, setExcessEntries] = useState([]);
  useEffect(() => {
    api.get("/excess-production/", { params: {} })
      .then(({ data }) => setExcessEntries(data || []))
      .catch(() => setExcessEntries([]));
  }, [open]);
  const excessStatusByPsId = useMemo(() => {
    const m = {};
    (excessEntries || []).forEach(e => {
      const pid = e.packing_slip_id;
      if (!pid) return;
      if (!m[pid]) m[pid] = { all_approved: true, statuses: [] };
      m[pid].statuses.push(e.status);
      if (e.status !== "Approved for Billing") m[pid].all_approved = false;
    });
    return m;
  }, [excessEntries]);
  const excessSelectedSlips = useMemo(() => {
    if (selectedSlipIds.size === 0) return [];
    return slips.filter(s => selectedSlipIds.has(s.id) && (
      (s.source_type || "") === "excess_so" ||
      (s.source_type || "") === "excess_eso" ||
      s.is_excess_dispatch === true
    ));
  }, [slips, selectedSlipIds]);
  const unauthorizedExcessSlips = useMemo(() =>
    excessSelectedSlips.filter(s => !(excessStatusByPsId[s.id] && excessStatusByPsId[s.id].all_approved)),
    [excessSelectedSlips, excessStatusByPsId]);
  const hasExcessSelected = unauthorizedExcessSlips.length > 0;
  const EXCESS_BLOCK_MSG = "This Packing List contains excess production. Excess quantities must be moved to General Stock or authorized via an Order Amendment before they can be included in a formal Packing List.";

  // Slips available for pull-down: attached to the current SO, not consumed elsewhere,
  // and (if a product filter is set) with at least one box in the picked product codes.
  const availableSlips = useMemo(() => {
    if (!form.order_id) return [];
    return slips.filter(s => {
      if (s.order_id !== form.order_id) return false;
      if (slipUsedInPl[s.id]) return false;
      if (productCodeFilter.size === 0) return true;
      return (s.boxes || []).some(b => productCodeFilter.has(b.product_code));
    });
  }, [slips, form.order_id, productCodeFilter, slipUsedInPl]);

  // Selected-slip totals (each slip contributes ALL of its boxes when ticked).
  const slipTotals = useMemo(() => {
    const t = { boxes: 0, qty: 0, net: 0, tare: 0, gross: 0 };
    slips.forEach(s => {
      if (!selectedSlipIds.has(s.id)) return;
      (s.boxes || []).forEach(b => {
        t.boxes += 1;
        t.qty += Number(b.meters) || 0;
        t.net += Number(b.net_weight) || 0;
        t.tare += Number(b.tare_weight) || 0;
        t.gross += Number(b.gross_weight) || ((Number(b.net_weight) || 0) + (Number(b.tare_weight) || 0));
      });
    });
    return t;
  }, [slips, selectedSlipIds]);

  // Build final BOX-#### rows for the PL from selected slips (each slip box -> one PL box).
  const buildBoxesFromSlips = (startIdx = 1) => {
    const boxes = [];
    let idx = startIdx;
    slips.forEach(s => {
      if (!selectedSlipIds.has(s.id)) return;
      (s.boxes || []).forEach(b => {
        boxes.push({
          box_no: `BOX-${String(idx).padStart(4, "0")}`,
          slip_no: s.slip_no,
          slip_box_no: b.box_no || "",
          article_no: b.product_code || "",
          item_no: b.item_id || "",
          // Feb 2026 · propagate HS Code from the Packing Slip box; fall
          // back to Product Master via hsCodeFor when the slip's own box
          // has no HS. Same pattern as the manual-entry generateBoxes.
          hs_code: b.hs_code || hsCodeFor(b.product_code) || "",
          meter: Number(b.meters) || 0,
          joints: Number(b.joints) || 0,
          net_wt: Number(b.net_weight) || 0,
          tare_wt: Number(b.tare_weight) || 0,
          gross_wt: Number(b.gross_weight) || ((Number(b.net_weight) || 0) + (Number(b.tare_weight) || 0)),
        });
        idx++;
      });
    });
    return boxes;
  };

  // Aggregate entries by product code — needed so the backend pending-vs-packed
  // calculation (which sums no_of_boxes × meter_per_box across all PLs) still
  // sees this PL's contribution to each SO line item.
  const buildEntriesFromSlips = () => {
    const o = orders.find(x => x.id === form.order_id);
    const soItems = o?.items || [];
    const agg = {};
    slips.forEach(s => {
      if (!selectedSlipIds.has(s.id)) return;
      (s.boxes || []).forEach(b => {
        const pc = b.product_code || "";
        if (!agg[pc]) {
          const li = soItems.find(x => x.article_no === pc);
          agg[pc] = {
            product_code: pc,
            item_no: li?.item_no || "",
            description: li?.description || "",
            colour: li?.colour || "",
            // Feb 2026 · aggregate HS Code so the backend pending-vs-packed
            // calculation persists it alongside the SO line's other fields.
            hs_code: hsCodeFor(pc) || "",
            order_qty: li ? (Number(li.order_qty) || 0) + (Number(li.extra_qty) || 0) : 0,
            boxes: 0, meters: 0, net: 0, tare: 0,
          };
        }
        agg[pc].boxes += 1;
        agg[pc].meters += Number(b.meters) || 0;
        agg[pc].net += Number(b.net_weight) || 0;
        agg[pc].tare += Number(b.tare_weight) || 0;
      });
    });
    return Object.values(agg).map(a => ({
      item_no: a.item_no,
      article_no: a.product_code,
      description: a.description,
      colour: a.colour,
      order_qty: a.order_qty,
      already_packed: 0,
      no_of_boxes: a.boxes,
      meter_per_box: a.boxes > 0 ? a.meters / a.boxes : 0,
      net_wt_per_box: a.boxes > 0 ? a.net / a.boxes : 0,
      tare_wt_per_box: a.boxes > 0 ? a.tare / a.boxes : 0,
    }));
  };

  const toggleSlipSelection = (sid) => {
    const next = new Set(selectedSlipIds);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setSelectedSlipIds(next);
  };
  const toggleProductCode = (pc) => {
    const next = new Set(productCodeFilter);
    if (next.has(pc)) next.delete(pc); else next.add(pc);
    setProductCodeFilter(next);
    // Deselect any slip that no longer matches the (non-empty) filter.
    if (next.size > 0) {
      const stillOk = new Set();
      slips.forEach(s => {
        if (!selectedSlipIds.has(s.id)) return;
        if ((s.boxes || []).some(b => next.has(b.product_code))) stillOk.add(s.id);
      });
      setSelectedSlipIds(stillOk);
    }
  };

  const save = async () => {
    if (!form.order_id) return toast.error("Sales order required");
    // Feb 2026 · Reserve the PL No at the LAST possible moment — mirrors
    // SO behaviour so cancelled drafts don't burn sequence gaps. Existing
    // rows keep their pl_no (editing path).
    let plNo = form.pl_no;
    if (!editing && !plNo) {
      try {
        const { data: nx } = await api.get("/next/packing-list-no");
        plNo = nx.pl_no;
      } catch (e) {
        return toast.error("Could not reserve a PL No — try again");
      }
    }
    const savedOrderId = form.order_id;

    // ---- Slips mode: derive entries/boxes from selected Packing Slips ----
    if (sourceMode === "slips") {
      if (selectedSlipIds.size === 0) return toast.error("Select at least one Packing Slip");
      const generatedEntries = buildEntriesFromSlips();
      const generatedBoxes = buildBoxesFromSlips(boxStartIdx);
      const t = slipTotals;
      const payload = {
        ...form,
        pl_no: plNo,
        entries: generatedEntries,
        source_mode: "slips",
        dispatch_source: dispatchSource,
        packing_slip_ids: Array.from(selectedSlipIds),
        no_of_packages: t.boxes,
        quantity: t.qty,
        net_weight: t.net,
        gross_weight: t.gross,
        box_start_no: boxStartIdx,
        box_end_no: boxStartIdx + t.boxes - 1,
        boxes: generatedBoxes,
      };
      try {
        if (editing) {
          await api.put(`/packing-lists/${editing.id}`, payload);
          toast.success("Updated");
        } else {
          await api.post("/packing-lists/", payload);
          if (onNextStep) {
            toast.success(`Packing List ${form.pl_no} created from ${selectedSlipIds.size} slip(s)`, {
              action: { label: "→ Create Delivery Challan", onClick: () => onNextStep("challans", savedOrderId) },
              duration: 8000,
            });
          } else {
            toast.success(`Packing List ${form.pl_no} created`);
          }
        }
        setOpen(false); load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
      return;
    }

    // ---- Manual mode: existing item-wise entries flow ----
    // Validate per-item: qty <= pending
    for (const e of form.entries) {
      const q = (Number(e.no_of_boxes) || 0) * (Number(e.meter_per_box) || 0);
      const pending = (Number(e.order_qty) || 0) - (Number(e.already_packed) || 0);
      if (q > pending + 1e-6) {
        return toast.error(`Item ${e.item_no} (${e.article_no}): ${q}m exceeds pending ${pending.toFixed(2)}m`);
      }
    }
    if (totals.boxes === 0) return toast.error("Add at least one box");
    try {
      const boxes_generated = generateBoxes(form.entries, boxStartIdx);
      const payload = {
        ...form,
        source_mode: "manual",
        dispatch_source: dispatchSource,
        packing_slip_ids: [],
        no_of_packages: totals.boxes,
        quantity: totals.qty,
        net_weight: totals.net,
        gross_weight: totals.gross,
        box_start_no: boxStartIdx,
        box_end_no: boxStartIdx + totals.boxes - 1,
        boxes: boxes_generated,
      };
      if (editing) {
        await api.put(`/packing-lists/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/packing-lists/", payload);
        // Workflow nudge — offer to jump to Delivery Challans with SO prefilled.
        if (onNextStep) {
          toast.success(`Packing List ${form.pl_no} created`, {
            action: {
              label: "→ Create Delivery Challan",
              onClick: () => onNextStep("challans", savedOrderId),
            },
            duration: 8000,
          });
        } else {
          toast.success(`Packing List ${form.pl_no} created`);
        }
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!rbacReady) return;
    if (!canDeletePl) { toast.error("🔒 sales_orders_packing.delete — ask an admin to grant this"); return; }
    const r = rows.find(x => x.id === id);
    if (r && plToDc[id]) {
      toast.error(`${r.pl_no} is dispatched via ${plToDc[id]} — cannot be deleted`);
      return;
    }
    if (!confirm("Delete?")) return;
    try {
      await api.delete(`/packing-lists/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const orderNo = (id) => orders.find(o => o.id === id)?.order_no || "—";
  // Look up the Customer's Style Number for a (order_id, product_code) pair
  // by scanning the SO's line items for a match on article_no / item_code.
  // Feeds the Style column on every PL preview + print (Feb 2026).
  const styleNoFor = (order_id, product_code) => {
    if (!order_id || !product_code) return "";
    const so = orders.find(o => o.id === order_id);
    if (!so || !Array.isArray(so.items)) return "";
    const pc = String(product_code).trim().toLowerCase();
    const styles = new Set(so.items.filter(li => {
      const cand = [li.article_no, li.item_code, li.item_no].filter(Boolean).map(s => String(s).trim().toLowerCase());
      return cand.includes(pc);
    }).map(li => String(li.style_no || "").trim()));
    // Exact-link rule (same as server): only one agreeing style for this code, else blank.
    return styles.size === 1 ? [...styles][0] : "";
  };

  const printPl = (r) => {
    const o = orders.find(x => x.id === r.order_id);
    const cname = r.customer_name || customerName(o?.customer_id);
    const cpo = r.customer_po_no || o?.po_number || "";
    const boxes = (r.boxes && r.boxes.length > 0) ? r.boxes : generateBoxes(r.entries || [], r.box_start_no || 1);
    // Enrich each box row with Style so the print/PDF column reads correctly.
    const enrichedBoxes = boxes.map(b => ({
      ...b,
      style_no: b.style_no || styleNoFor(r.order_id, b.article_no),
      // Feb 2026 · make sure the printed PDF always carries the HS Code,
      // falling back to Product Master when the saved box was created
      // before the HS-capture patch and doesn't have one on record.
      hs_code: b.hs_code || hsCodeFor(b.article_no) || "",
    }));
    const totalJoints = enrichedBoxes.reduce((a, b) => a + (Number(b.joints) || 0), 0);
    printSignedDocument("PL", r.id, {
      kind: "Packing List",
      no: r.pl_no,
      date: r.pack_date,
      meta: [
        { label: "Customer Name", value: cname || "—" },
        { label: "Customer PO No", value: cpo || "—" },
        { label: "Sales Order", value: orderNo(r.order_id) },
        { label: "Total Boxes", value: r.no_of_packages },
        { label: "Quantity (m)", value: fmtQty(r.quantity, "MTR") },
        { label: "Net Weight (kg)", value: fmtQty(r.net_weight, "KG") },
        { label: "Gross Weight (kg)", value: fmtQty(r.gross_weight, "KG") },
      ],
      sections: [
        {
          heading: `Box-wise Packing Detail · ${enrichedBoxes.length} Boxes`,
          // Column order mirrors the on-screen View dialog exactly (Feb 2026):
          // Box No · Slip No · Product Code · Style · HS Code · Meter/Box · Joints · Net Wt · Tare Wt · Gross Wt
          columns: [
            { key: "box_no", label: "Box No", mono: true },
            { key: "slip_no", label: "Slip No", mono: true, fmt: (v, row) => v ? (row.slip_box_no ? `${v} · ${row.slip_box_no}` : v) : "—" },
            { key: "article_no", label: "Product Code", mono: true },
            { key: "style_no", label: "Style No", mono: true, fmt: v => v || "—" },
            { key: "hs_code", label: "HS Code", mono: true, fmt: v => v || "—" },
            { key: "meter", label: "Meter/Box", align: "right", fmt: v => fmtQty(v || 0, "MTR") },
            { key: "joints", label: "Joints", align: "right", fmt: v => Number(v || 0) },
            { key: "net_wt", label: "Net Wt", align: "right", fmt: v => fmtQty(v || 0, "KG") },
            { key: "tare_wt", label: "Tare Wt", align: "right", fmt: v => fmtQty(v || 0, "KG") },
            { key: "gross_wt", label: "Gross Wt", align: "right", fmt: v => fmtQty(v || 0, "KG") },
          ],
          rows: enrichedBoxes,
          footerRow: {
            box_no: `Total ${enrichedBoxes.length} boxes`,
            meter: fmtQty(r.quantity, "MTR"),
            joints: totalJoints,
            net_wt: fmtQty(r.net_weight, "KG"),
            tare_wt: fmtQty(Number(r.gross_weight) - Number(r.net_weight), "KG"),
            gross_wt: fmtQty(r.gross_weight, "KG"),
          },
        },
      ],
      footer: r.remarks ? `Remarks: ${r.remarks}` : "",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 mr-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filter</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" data-testid="pl-status-filter">
            {[
              { k: "all", label: "All", count: rows.length },
              { k: "draft", label: "Draft", count: rows.filter(r => !plToDc[r.id]).length },
              { k: "dispatched", label: "Dispatched", count: rows.filter(r => !!plToDc[r.id]).length },
            ].map(t => (
              <button
                key={t.k}
                data-testid={`pl-filter-${t.k}`}
                onClick={() => setStatusFilter(t.k)}
                className={`px-3 h-8 text-xs font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${statusFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
              >
                {t.label}
                <span className={`ml-1.5 font-mono text-[10px] ${statusFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">Sort</div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="pl-sort" className="bg-[var(--bg-0)] border-[var(--border)] h-8 text-xs w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
              <SelectItem value="date_desc">Date · newest first</SelectItem>
              <SelectItem value="date_asc">Date · oldest first</SelectItem>
              <SelectItem value="pl_asc">PL No · A → Z</SelectItem>
              <SelectItem value="pl_desc">PL No · Z → A</SelectItem>
              <SelectItem value="boxes_desc">Total Boxes · high → low</SelectItem>
              <SelectItem value="qty_desc">Quantity · high → low</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative ml-2 w-64">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="pl-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="PL No / SO / Customer / PO / Article No…"
              className="w-full h-8 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          {search && (
            <button data-testid="pl-search-clear" onClick={() => setSearch("")} className="h-8 px-2 text-[11px] text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
          )}
        </div>
        <button data-testid="new-pl-btn" onClick={openNew} disabled={!rbacReady || !canCreatePl} title={rbacReady && !canCreatePl ? "🔒 sales_orders_packing.create — ask an admin to grant this" : undefined} className={`h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 ${(!rbacReady || !canCreatePl) ? "opacity-50 cursor-not-allowed" : ""}`}>
          <PlusIcon size={14} weight="bold" />New Packing List
        </button>
        <button
          onClick={() => exportToCSV("packing-lists.csv", rows.map(r => ({ ...r, order_no: orderNo(r.order_id), status: plToDc[r.id] ? `Dispatched · ${plToDc[r.id]}` : "Draft" })), [
            { key: "pl_no", label: "PL No" }, { key: "order_no", label: "Sales Order" }, { key: "pack_date", label: "Date" }, { key: "status", label: "Status" },
            { key: "no_of_packages", label: "Boxes" }, { key: "gross_weight", label: "Gross Wt" },
            { key: "net_weight", label: "Net Wt" }, { key: "quantity", label: "Qty" }, { key: "remarks", label: "Remarks" },
          ])}
          className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"
        >
          <DownloadSimpleIcon size={14} weight="bold" />Export CSV
        </button>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">PL No</th>
              <th className="text-left px-4 py-3">PL Date</th>
              <th className="text-left px-4 py-3">SO No</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">PO No</th>
              <th className="text-left px-4 py-3">Source Godown</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Total Boxes</th>
              <th className="text-right px-4 py-3">Qty (m)</th>
              <th className="text-right px-4 py-3">Net Wt (kg)</th>
              <th className="text-right px-4 py-3">Gross Wt (kg)</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const q = search.trim().toLowerCase();
              const filtered = rows.filter(r => {
                if (statusFilter === "draft" && plToDc[r.id]) return false;
                if (statusFilter === "dispatched" && !plToDc[r.id]) return false;
                if (q) {
                  const so = orderNo(r.order_id) || "";
                  const cname = r.customer_name || customerName(orders.find(o => o.id === r.order_id)?.customer_id) || "";
                  const cpo = r.customer_po_no || (orders.find(o => o.id === r.order_id)?.po_number) || "";
                  const articles = [
                    ...(r.entries || []).map(e => e.article_no || ""),
                    ...(r.boxes || []).map(b => b.article_no || ""),
                  ].join(" ");
                  const hay = `${r.pl_no} ${so} ${cname} ${cpo} ${articles}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              const sorted = [...filtered].sort((a, b) => {
                switch (sortBy) {
                  case "date_asc": return (a.pack_date || "").localeCompare(b.pack_date || "");
                  case "date_desc": return (b.pack_date || "").localeCompare(a.pack_date || "");
                  case "pl_asc": return (a.pl_no || "").localeCompare(b.pl_no || "");
                  case "pl_desc": return (b.pl_no || "").localeCompare(a.pl_no || "");
                  case "boxes_desc": return (Number(b.no_of_packages) || 0) - (Number(a.no_of_packages) || 0);
                  case "qty_desc": return (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
                  default: return 0;
                }
              });
              return sorted.map(r => {
              const dcNo = plToDc[r.id];
              // Feb 2026 · Show Customer + PO No so the packing team
              // doesn't have to cross-reference the SO grid.
              const _o = orders.find(o => o.id === r.order_id);
              const _cname = r.customer_name || customerName(_o?.customer_id) || "—";
              const _cpo   = r.customer_po_no || (_o?.po_number) || "—";
              return (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`pl-${r.pl_no}`}>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.pl_no} className="text-white" /></td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.pack_date}</td>
                <td className="px-4 py-3 font-mono">{orderNo(r.order_id)}</td>
                <td className="px-4 py-3" data-testid={`pl-customer-${r.pl_no}`}>{_cname}</td>
                <td className="px-4 py-3 font-mono text-zinc-400" data-testid={`pl-po-${r.pl_no}`}>{_cpo}</td>
                <td className="px-4 py-3" data-testid={`pl-source-${r.pl_no}`}>
                  {r.source_godown_code === "FG-STORE" ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />FG-STORE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{r.source_godown_code || "PACK-1FLR"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3" data-testid={`pl-status-${r.pl_no}`}>
                  {dcNo ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Dispatched · {dcNo}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Draft
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.no_of_packages}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.net_weight).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.gross_weight).toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <RowActionsMenu testId={`pl-actions-${r.pl_no}`}>
                  <button onClick={() => setPreview(r)} title="View packing list" className="px-2 text-xs text-[var(--accent)]">View</button>
                  <button onClick={() => printPl(r)} data-testid={`print-pl-${r.pl_no}`} title="Print / PDF" className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-[var(--accent)]"><PrinterIcon size={14} /></button>
                  <DocDownloadIcons type="PL" id={r.id} testPrefix={`pl-row-${r.pl_no}`} />
                  {canCreatePl && (
                    <button
                      data-testid={`bulk-slips-pl-${r.pl_no}`}
                      onClick={() => {
                        // Bulk-from-PL handoff (Feb 2026) — pulls SO + first box's
                        // article/style so the warehouse can re-issue N identical
                        // slips against an already-packed PL without retyping.
                        const so = orders.find(o => o.id === r.order_id);
                        const firstBox = (r.boxes && r.boxes[0]) || (r.entries && r.entries[0]) || {};
                        const payload = {
                          order_id: r.order_id || "",
                          customer_id: so?.customer_id || "",
                          product_code: firstBox.article_no || firstBox.product_code || "",
                          item_id: firstBox.article_id || firstBox.item_id || "",
                          style_no: firstBox.style_no || "",
                        };
                        try { sessionStorage.setItem("mes_ps_next_bulk", JSON.stringify(payload)); }
                        catch (e) { /* noop */ }
                        onNextStep && onNextStep("slips");
                        toast.info(`Opening Bulk Slips for ${r.pl_no} · ${payload.product_code || "PL"}`);
                      }}
                      title="Bulk-generate Packing Slips for this PL"
                      className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"
                    ><PlusIcon size={14} weight="bold" /></button>
                  )}
                  {dcNo ? (
                    <span title={`Locked · Dispatched via ${dcNo}`} data-testid={`pl-locked-${r.pl_no}`} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-emerald-400/70 text-xs"><LockIcon size={14} weight="bold" />Locked · {dcNo}</span>
                  ) : (
                    <>
                      <button data-testid={`edit-pl-${r.pl_no}`} title="Edit" onClick={() => openEdit(r)} className="p-1.5 rounded-sm hover:bg-[var(--bg-2)]"><PencilIcon size={14} /></button>
                      <button data-testid={`delete-pl-${r.pl_no}`} onClick={() => remove(r.id)} disabled={!rbacReady || !canDeletePl} title={rbacReady && !canDeletePl ? "🔒 sales_orders_packing.delete — ask an admin to grant this" : "Delete"} className={`p-1.5 rounded-sm hover:bg-red-500/10 text-red-400 ${(!rbacReady || !canDeletePl) ? "opacity-40 cursor-not-allowed" : ""}`}><TrashIcon size={14} /></button>
                    </>
                  )}
                  </RowActionsMenu>
                </td>
              </tr>
              );
              });
            })()}
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-14 text-zinc-500">No packing lists yet — click New Packing List.</td></tr>}
            {rows.length > 0 && (() => {
              const q = search.trim().toLowerCase();
              const visible = rows.filter(r => {
                if (statusFilter === "draft" && plToDc[r.id]) return false;
                if (statusFilter === "dispatched" && !plToDc[r.id]) return false;
                if (q) {
                  const so = orderNo(r.order_id) || "";
                  const cname = r.customer_name || customerName(orders.find(o => o.id === r.order_id)?.customer_id) || "";
                  const cpo = r.customer_po_no || (orders.find(o => o.id === r.order_id)?.po_number) || "";
                  const articles = [
                    ...(r.entries || []).map(e => e.article_no || ""),
                    ...(r.boxes || []).map(b => b.article_no || ""),
                  ].join(" ");
                  const hay = `${r.pl_no} ${so} ${cname} ${cpo} ${articles}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              return visible.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-500">No packing lists match the current filter{q ? ` / search "${search}"` : ""}.</td></tr>
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto" description="Manage extra-quantity approvals, cancel-balance and short-close requests on this order.">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Packing List</DialogTitle></DialogHeader>

          {/* Customer header banner */}
          {(form.customer_name || form.customer_po_no) && (
            <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3" data-testid="pl-customer-header">
              <div>
                <div className="overline text-[10px] mb-0.5">Customer Name</div>
                <div className="font-semibold text-sm" data-testid="pl-customer-name">{form.customer_name || "—"}</div>
              </div>
              <div>
                <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                <div className="font-mono text-sm" data-testid="pl-customer-po">{form.customer_po_no || "—"}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <F label="PL No (auto)"><input
              data-testid="pl-no"
              value={form.pl_no}
              readOnly
              placeholder="Auto · assigned on save"
              className={inp + " opacity-70 cursor-not-allowed bg-[var(--bg-2)]/60 font-mono"}
              title="Auto-assigned on save — mirrors SO No behaviour. Cancel this draft without leaving a gap in the sequence."
            /></F>
            <F label="Date"><input type="date" value={form.pack_date} onChange={e => setForm({ ...form, pack_date: e.target.value })} className={inp} /></F>
            <F label="Sales Order">
              <Select value={form.order_id} onValueChange={onOrderChange}>
                <SelectTrigger data-testid="pl-order" className={selCls}><SelectValue placeholder="Select order" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {orders.filter(o => o.order_against !== "stock").map(o => {  /* SOESO-FULFIL-01 · Stock SO is never a PL source */
                    const pending = soPending[o.id] ?? 0;
                    const isFull = pending <= 0;
                    return (
                      <SelectItem key={o.id} value={o.id} disabled={isFull} data-testid={`pl-order-opt-${o.order_no}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="font-mono">{o.order_no}</span>
                          {isFull ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 uppercase tracking-widest">Fully packed</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 font-mono">Pending {Number(pending).toLocaleString("en-IN", { maximumFractionDigits: 0 })}m</span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </F>
          </div>

          {/* Source-mode toggle (Feb 2026) — pick between manual item-wise entries
              or pulling boxes from existing Packing Slips. Hidden until an SO is picked. */}
          {form.order_id && (
            <div className="mt-4 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40 p-2 flex items-center gap-2 flex-wrap">
              <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Dispatch From</span>
              <button
                type="button"
                data-testid="pl-dispatch-packing-floor"
                onClick={() => setDispatchSource("PACKING-FLOOR")}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${dispatchSource === "PACKING-FLOOR" ? "bg-amber-500 text-black" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
                title="Path A · Pick packing slips directly from the packing floor"
              >Packing Floor</button>
              <button
                type="button"
                data-testid="pl-dispatch-fg-store"
                onClick={() => setDispatchSource("FG-STORE")}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${dispatchSource === "FG-STORE" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
                title="Path B · Standard dispatch from FG store"
              >FG-STORE</button>
              <span className="ml-auto text-[10px] font-mono text-zinc-500" data-testid="pl-source-godown-hint">
                Outward godown → <span className="text-zinc-300">{dispatchSource === "FG-STORE" ? "FG-STORE" : "PACK-1FLR"}</span>
              </span>
              <span className="w-full h-px bg-[var(--border)] my-1" />
              <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Source</span>
              <button
                type="button"
                data-testid="pl-mode-manual"
                onClick={() => setSourceMode("manual")}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceMode === "manual" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
              >Manual Entry</button>
              <button
                type="button"
                data-testid="pl-mode-slips"
                onClick={() => setSourceMode("slips")}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceMode === "slips" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
              >Pull from Packing Slips <span className="ml-1 opacity-70 font-mono">({availableSlips.length})</span></button>
            </div>
          )}

          {/* Item entries */}
          {sourceMode === "manual" && form.entries.length > 0 && (
            <div className="mt-4">
              <div className="overline mb-2">Item-wise Packing (from Sales Order)</div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--bg-2)] uppercase tracking-widest text-[10px] text-zinc-500">
                      <th className="px-2 py-2 text-left w-14">Item</th>
                      <th className="px-2 py-2 text-left">Product Code</th>
                      <th className="px-2 py-2 text-left">HS Code</th>
                      <th className="px-2 py-2 text-right">Order Qty</th>
                      <th className="px-2 py-2 text-right text-zinc-400">Already Packed</th>
                      <th className="px-2 py-2 text-right bg-amber-500/10 text-amber-400">Pending</th>
                      <th className="px-2 py-2 text-right bg-pink-500/10">No of Boxes</th>
                      <th className="px-2 py-2 text-right bg-pink-500/10">Meter / Box</th>
                      <th className="px-2 py-2 text-right">Net Wt / Box</th>
                      <th className="px-2 py-2 text-right">Tare Wt / Box</th>
                      <th className="px-2 py-2 text-right bg-emerald-500/10">Gross Wt / Box</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.entries.map((e, i) => {
                      const gpb = (Number(e.net_wt_per_box) || 0) + (Number(e.tare_wt_per_box) || 0);
                      const packed = Number(e.already_packed) || 0;
                      const pending = (Number(e.order_qty) || 0) - packed;
                      const currentQty = (Number(e.no_of_boxes) || 0) * (Number(e.meter_per_box) || 0);
                      const over = currentQty > pending + 1e-6;
                      return (
                        <tr key={e.item_no || `pl-entry-${i}`} className="border-t border-[var(--border)]">
                          <td className="px-1 py-1 font-mono text-center">{e.item_no}</td>
                          <td className="px-2 py-1 font-mono text-[11px]">{e.article_no || <span className="text-zinc-500">—</span>}</td>
                          <td className="px-1 py-1"><input data-testid={`pl-hs-${i}`} value={e.hs_code || ""} onChange={ev => updateEntry(i, "hs_code", ev.target.value)} placeholder="Auto" className={inp + " font-mono text-[11px]"} title="Auto-pulled from Product Master · edit to override" /></td>
                          <td className="px-2 py-1 text-right font-mono">{e.order_qty}</td>
                          <td className="px-2 py-1 text-right font-mono text-zinc-400" data-testid={`pl-packed-${i}`}>{packed.toFixed(0)}</td>
                          <td className={`px-2 py-1 text-right font-mono ${pending <= 0 ? "text-zinc-500" : "text-amber-400"}`} data-testid={`pl-pending-${i}`}>{pending.toFixed(0)}</td>
                          <td className="px-1 py-1"><FormattedNumberInput data-testid={`pl-boxes-${i}`} value={e.no_of_boxes} onChange={ev => updateEntry(i, "no_of_boxes", ev.target.value)} className={inp + " text-right font-mono" + (over ? " !border-red-500 text-red-400" : "")} /></td>
                          <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`pl-mpb-${i}`} value={e.meter_per_box} onChange={ev => updateEntry(i, "meter_per_box", ev.target.value)} className={inp + " text-right font-mono" + (over ? " !border-red-500 text-red-400" : "")} /></td>
                          <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`pl-netpb-${i}`} value={e.net_wt_per_box} onChange={ev => updateEntry(i, "net_wt_per_box", ev.target.value)} className={inp + " text-right font-mono"} /></td>
                          <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`pl-tarepb-${i}`} value={e.tare_wt_per_box} onChange={ev => updateEntry(i, "tare_wt_per_box", ev.target.value)} className={inp + " text-right font-mono"} /></td>
                          <td className="px-2 py-2 text-right font-mono bg-emerald-500/5">{gpb.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[var(--bg-2)] font-medium">
                      <td colSpan={6} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</td>
                      <td className="px-2 py-2 text-right font-mono bg-pink-500/10">{totals.boxes}</td>
                      <td className="px-2 py-2 text-right font-mono">{totals.qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} m</td>
                      <td className="px-2 py-2 text-right font-mono">{totals.net.toFixed(2)}</td>
                      <td className="px-2 py-2 text-right font-mono">{totals.tare.toFixed(2)}</td>
                      <td className="px-2 py-2 text-right font-mono bg-emerald-500/10">{totals.gross.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Generated Box-wise List Preview */}
              {totals.boxes > 0 && (
                <div className="mt-4">
                  <div className="overline mb-2 flex items-center gap-2">
                    <span>Generated Packing List · {totals.boxes} boxes</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)]" data-testid="pl-box-range">
                      BOX-{String(boxStartIdx).padStart(4, "0")} → BOX-{String(boxStartIdx + totals.boxes - 1).padStart(4, "0")}
                    </span>
                    {boxStartIdx > 1 && (
                      <span className="text-[10px] text-zinc-500">(continues from previous packing lists · box #{boxStartIdx - 1})</span>
                    )}
                  </div>
                  <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[var(--bg-2)]">
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                          <th className="px-2 py-2 text-left">Box No</th>
                          <th className="px-2 py-2 text-left">Product Code</th>
                          <th className="px-2 py-2 text-left">Style No</th>
                          <th className="px-2 py-2 text-right">Meter/Box</th>
                          <th className="px-2 py-2 text-right">Net Wt</th>
                          <th className="px-2 py-2 text-right">Tare Wt</th>
                          <th className="px-2 py-2 text-right">Gross Wt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generateBoxes(form.entries, boxStartIdx).map(b => (
                          <tr key={b.box_no} className="border-t border-[var(--border)]" data-testid={`box-${b.box_no}`}>
                            <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{styleNoFor(form.order_id, b.article_no) || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.meter}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.net_wt.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.tare_wt.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.gross_wt.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="sticky bottom-0 bg-[var(--bg-2)]">
                        <tr className="font-medium border-t border-[var(--border)]">
                          <td className="px-2 py-2 text-xs uppercase tracking-widest text-zinc-500">Total {totals.boxes} boxes</td>
                          <td></td>
                          <td></td>
                          <td className="px-2 py-2 text-right font-mono">{totals.qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} m</td>
                          <td className="px-2 py-2 text-right font-mono">{totals.net.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right font-mono">{totals.tare.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right font-mono">{totals.gross.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <F label="Remarks" className="mt-3"><input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} /></F>

          {/* ---- Slips picker (Feb 2026) ---- */}
          {sourceMode === "slips" && form.order_id && (
            <div className="mt-4">
              {/* Product Code multi-select chips */}
              <div className="mb-3">
                <div className="overline mb-2 flex items-center gap-2">
                  <span>Product Code Filter</span>
                  <span className="text-[10px] text-zinc-500 normal-case tracking-normal">Tick any to narrow slips · empty = show all</span>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="pl-product-code-chips">
                  {soProductCodes.length === 0 && (
                    <span className="text-xs text-zinc-500 italic">No product codes on this order yet</span>
                  )}
                  {soProductCodes.map(pc => {
                    const on = productCodeFilter.has(pc);
                    return (
                      <button
                        key={pc}
                        type="button"
                        data-testid={`pl-pc-chip-${pc}`}
                        onClick={() => toggleProductCode(pc)}
                        className={`px-2 py-1 rounded-sm text-[11px] font-mono border transition-colors ${on ? "bg-[var(--accent)]/20 border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400 hover:border-zinc-500"}`}
                      >{pc}</button>
                    );
                  })}
                  {productCodeFilter.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setProductCodeFilter(new Set())}
                      className="px-2 py-1 rounded-sm text-[11px] border border-transparent text-zinc-500 hover:text-zinc-300"
                    >Clear</button>
                  )}
                </div>
              </div>

              {/* Available slips table */}
              <div className="overline mb-2 flex items-center gap-2">
                <span>Available Packing Slips</span>
                <span className="text-[10px] font-mono text-zinc-500">{selectedSlipIds.size} of {availableSlips.length} selected</span>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[var(--bg-2)] z-10">
                    <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                      <th className="px-2 py-2 w-8 text-left">
                        <input
                          type="checkbox"
                          data-testid="pl-slip-select-all"
                          checked={availableSlips.length > 0 && availableSlips.every(s => selectedSlipIds.has(s.id))}
                          onChange={(e) => {
                            const next = new Set(selectedSlipIds);
                            if (e.target.checked) availableSlips.forEach(s => next.add(s.id));
                            else availableSlips.forEach(s => next.delete(s.id));
                            setSelectedSlipIds(next);
                          }}
                        />
                      </th>
                      <th className="px-2 py-2 text-left">Slip No</th>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Product Code(s)</th>
                      <th className="px-2 py-2 text-right">Boxes</th>
                      <th className="px-2 py-2 text-right">Meters</th>
                      <th className="px-2 py-2 text-right">Net Wt</th>
                      <th className="px-2 py-2 text-right">Gross Wt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableSlips.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-6 text-zinc-500">
                        {form.order_id ? "No unlinked packing slips found for this order" : "Pick a Sales Order to see slips"}
                      </td></tr>
                    )}
                    {availableSlips.map(s => {
                      const on = selectedSlipIds.has(s.id);
                      const pcs = Array.from(new Set((s.boxes || []).map(b => b.product_code).filter(Boolean)));
                      const meters = (s.boxes || []).reduce((a, b) => a + (Number(b.meters) || 0), 0);
                      const net = (s.boxes || []).reduce((a, b) => a + (Number(b.net_weight) || 0), 0);
                      const gross = (s.boxes || []).reduce((a, b) => a + (Number(b.gross_weight) || (Number(b.net_weight) || 0) + (Number(b.tare_weight) || 0)), 0);
                      return (
                        <tr
                          key={s.id}
                          data-testid={`pl-slip-row-${s.slip_no}`}
                          onClick={() => toggleSlipSelection(s.id)}
                          className={`border-t border-[var(--border)] cursor-pointer transition-colors ${on ? "bg-[var(--accent)]/10" : "hover:bg-[var(--bg-2)]/50"}`}
                        >
                          <td className="px-2 py-2"><input type="checkbox" checked={on} onChange={() => toggleSlipSelection(s.id)} data-testid={`pl-slip-cb-${s.slip_no}`} /></td>
                          <td className="px-2 py-2 font-mono font-semibold">{s.slip_no}</td>
                          <td className="px-2 py-2 font-mono text-zinc-400">{s.pack_date}</td>
                          <td className="px-2 py-2 font-mono text-[11px]">{pcs.join(", ") || "—"}</td>
                          <td className="px-2 py-2 text-right font-mono">{(s.boxes || []).length}</td>
                          <td className="px-2 py-2 text-right font-mono">{meters.toFixed(0)}</td>
                          <td className="px-2 py-2 text-right font-mono">{net.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right font-mono">{gross.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-[var(--bg-2)]">
                    <tr className="font-medium border-t border-[var(--border)]">
                      <td colSpan={4} className="px-2 py-2 text-right text-xs uppercase tracking-widest text-zinc-500">Selected Totals</td>
                      <td className="px-2 py-2 text-right font-mono bg-pink-500/10" data-testid="pl-slip-tot-boxes">{slipTotals.boxes}</td>
                      <td className="px-2 py-2 text-right font-mono" data-testid="pl-slip-tot-meters">{slipTotals.qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} m</td>
                      <td className="px-2 py-2 text-right font-mono">{slipTotals.net.toFixed(2)}</td>
                      <td className="px-2 py-2 text-right font-mono bg-emerald-500/10">{slipTotals.gross.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Preview generated PL box list */}
              {slipTotals.boxes > 0 && (
                <div className="mt-4">
                  <div className="overline mb-2 flex items-center gap-2">
                    <span>Generated Packing List · {slipTotals.boxes} boxes</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)]" data-testid="pl-slip-box-range">
                      BOX-{String(boxStartIdx).padStart(4, "0")} → BOX-{String(boxStartIdx + slipTotals.boxes - 1).padStart(4, "0")}
                    </span>
                    {boxStartIdx > 1 && (
                      <span className="text-[10px] text-zinc-500">(continues from previous packing lists · box #{boxStartIdx - 1})</span>
                    )}
                  </div>
                  <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[var(--bg-2)]">
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                          <th className="px-2 py-2 text-left">Box No</th>
                          <th className="px-2 py-2 text-left">Slip · Box</th>
                          <th className="px-2 py-2 text-left">Product Code</th>
                          <th className="px-2 py-2 text-left">Style No</th>
                          <th className="px-2 py-2 text-left">HS Code</th>
                          <th className="px-2 py-2 text-right">Meter</th>
                          <th className="px-2 py-2 text-right">Joints</th>
                          <th className="px-2 py-2 text-right">Net Wt</th>
                          <th className="px-2 py-2 text-right">Tare Wt</th>
                          <th className="px-2 py-2 text-right">Gross Wt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildBoxesFromSlips(boxStartIdx).map(b => (
                          <tr key={b.box_no} className="border-t border-[var(--border)]" data-testid={`pl-slip-box-${b.box_no}`}>
                            <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-400">{b.slip_no}{b.slip_box_no ? ` · ${b.slip_box_no}` : ""}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{styleNoFor(form.order_id, b.article_no) || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-300" data-testid={`pl-slip-hs-${b.box_no}`}>{b.hs_code || hsCodeFor(b.article_no) || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.meter).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.joints || 0)}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.net_wt.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.tare_wt.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{b.gross_wt.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button
              data-testid="save-pl"
              onClick={() => { if (hasExcessSelected) { alert(EXCESS_BLOCK_MSG); return; } save(); }}
              disabled={hasExcessSelected}
              title={hasExcessSelected ? EXCESS_BLOCK_MSG : ""}
              className={`px-4 py-2 rounded-sm text-sm font-semibold text-white ${hasExcessSelected ? "bg-zinc-700 cursor-not-allowed opacity-60" : "bg-[var(--accent)]"}`}
            >{hasExcessSelected ? "Blocked · Excess Dispatch in selection" : "Save Packing List"}</button>
          </DialogFooter>
          {hasExcessSelected && (
            <div className="mt-2 p-3 rounded-sm border border-red-500/60 bg-red-500/10 text-[11px] text-red-200" data-testid="pl-excess-block-banner">
              <div className="font-semibold mb-1 text-red-300">Packing List Lock · {unauthorizedExcessSlips.length} un-authorized excess slip{unauthorizedExcessSlips.length === 1 ? "" : "s"} in selection</div>
              <div>{EXCESS_BLOCK_MSG}</div>
              <div className="mt-1.5 font-mono text-[10px] text-red-300/80">Un-authorized: {unauthorizedExcessSlips.map(s => s.slip_no).join(", ")}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" description="Manage extra-quantity approvals, cancel-balance and short-close requests on this order.">
          <DialogHeader><DialogTitle className="font-display">Packing List — {preview?.pl_no}</DialogTitle></DialogHeader>
          {preview && (
            <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
              <div>
                <div className="overline text-[10px] mb-0.5">Customer Name</div>
                <div className="font-semibold text-sm">{preview.customer_name || customerName(orders.find(o => o.id === preview.order_id)?.customer_id) || "—"}</div>
              </div>
              <div>
                <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                <div className="font-mono text-sm">{preview.customer_po_no || orders.find(o => o.id === preview.order_id)?.po_number || "—"}</div>
              </div>
            </div>
          )}
          <div className="text-xs text-zinc-400 mb-3">
            <span className="mr-4">Order: <span className="font-mono">{orderNo(preview?.order_id)}</span></span>
            <span className="mr-4">Date: <span className="font-mono">{preview?.pack_date}</span></span>
            <span>Total Boxes: <span className="font-mono">{preview?.no_of_packages}</span></span>
          </div>
          {preview && (
            <DocumentInfo docNo={preview.pl_no} createdAt={preview.created_at} updatedAt={preview.updated_at} createdBy={preview.created_by} docLabel="Packing List No." testidPrefix="pl" />
          )}
          {preview && (
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[var(--bg-2)]">
                  <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                    <th className="px-2 py-2 text-left">Box No</th>
                    <th className="px-2 py-2 text-left">Slip No</th>
                    <th className="px-2 py-2 text-left">Product Code</th>
                    <th className="px-2 py-2 text-left">Style No</th>
                    <th className="px-2 py-2 text-left">HS Code</th>
                    <th className="px-2 py-2 text-right">Meter/Box</th>
                    <th className="px-2 py-2 text-right">Joints</th>
                    <th className="px-2 py-2 text-right">Net Wt</th>
                    <th className="px-2 py-2 text-right">Tare Wt</th>
                    <th className="px-2 py-2 text-right">Gross Wt</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const boxes = (preview.boxes && preview.boxes.length > 0)
                      ? preview.boxes
                      : generateBoxes(preview.entries, preview.box_start_no || 1);
                    const totalJoints = boxes.reduce((a, b) => a + (Number(b.joints) || 0), 0);
                    return (
                      <>
                        {boxes.map(b => (
                          <tr key={b.box_no} className="border-t border-[var(--border)]" data-testid={`preview-box-${b.box_no}`}>
                            <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-400">{b.slip_no ? (b.slip_box_no ? `${b.slip_no} · ${b.slip_box_no}` : b.slip_no) : <span className="text-zinc-600">—</span>}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || "—"}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{(b.style_no || styleNoFor(preview.order_id, b.article_no)) || "—"}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-300">{b.hs_code || hsCodeFor(b.article_no) || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.meter || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.joints || 0)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.net_wt || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.tare_wt || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.gross_wt || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="font-medium border-t border-[var(--border)] bg-[var(--bg-2)]">
                          <td colSpan={5} className="px-2 py-2 text-xs uppercase tracking-widest text-zinc-500">Total {preview.no_of_packages} boxes</td>
                          <td className="px-2 py-2 text-right font-mono">{Number(preview.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })} m</td>
                          <td className="px-2 py-2 text-right font-mono">{totalJoints}</td>
                          <td className="px-2 py-2 text-right font-mono">{Number(preview.net_weight).toFixed(2)}</td>
                          <td className="px-2 py-2 text-right font-mono">{(Number(preview.gross_weight) - Number(preview.net_weight)).toFixed(2)}</td>
                          <td className="px-2 py-2 text-right font-mono">{Number(preview.gross_weight).toFixed(2)}</td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => preview && printPl(preview)} className="px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
            {preview?.id && <DocDownloadButtons type="PL" id={preview.id} testPrefix="pl-preview" />}
            <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DeliveryChallansTab({ onNextStep, isExport = false } = {}) {
  // Mar 2, 2026 · When mounted inside the Exports module (`isExport=true`),
  // the SO picker MUST list ONLY Export Sales Orders (ESO). Domestic SOs
  // are excluded to prevent operators from cross-linking a DC to a
  // wrong-context order. The rest of the flow stays identical — ESO rows
  // are normalised to the same shape as SO (`order_no`, `customer_id`,
  // `po_number`, `items[]` with `rate`) so downstream helpers just work.
  const { hasPerm } = useAuth();
  // Aug 31, 2026 · Reactive perm gate for the DC tab — same pattern.
  const { can: rbacCan, ready: rbacReady } = useRbac();
  const canCreateDc = rbacCan("sales_orders_challans", "create") || rbacCan("sales_orders_challans", "edit") || rbacCan("dispatch", "edit");
  // Aug 31, 2026 · Reactive Delete gate — parity with canCreateDc
  // V28-004B · Delete permission aligned to the single authoritative backend
  // permission `sales_orders_challans:delete` (removed the broader dispatch:*
  // OR-set that the backend never accepted — UI now matches the server).
  const isEdcRow = (r) => String(r?.dc_no || "").toUpperCase().startsWith("EDC-");
  // EDC-DEL-01 · EDC → exports_dc.delete, DC → sales_orders_challans.delete (matches backend).
  const deletePermFor = (r) => (isEdcRow(r) ? "exports_dc.delete" : "sales_orders_challans.delete");
  const canDeleteDc = (r) => (isEdcRow(r) ? rbacCan("exports_dc", "delete") : rbacCan("sales_orders_challans", "delete"));
  const canEditDc = rbacCan("sales_orders_challans", "edit");
  const isReversedDc = (r) => !!(r?.delivery_reversed || r?.void || String(r?.status || "").toUpperCase() === "DELIVERY_REVERSED");
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pls, setPls] = useState([]);
  const [customers, setCustomers] = useState([]);
  // Feb 2026 · Articles feed the HS Code lookup for the DC summary + Tax Invoice.
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isAmending, setIsAmending] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [historyDc, setHistoryDc] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [search, setSearch] = useState("");
  const empty = {
    dc_no: "", order_id: "", packing_list_id: "", packing_list_ids: [],
    customer_name: "", customer_po_no: "",
    challan_date: new Date().toISOString().slice(0, 10),
    vehicle_no: "", lr_no: "", driver_name: "", transporter: "", quantity: 0, remarks: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    // Sep 03, 2026 · 403-tolerant Promise.all — mirrors PackingListsTab.
    const [d, o, p, c, a] = await Promise.all([
      api.get("/delivery-challans/").catch(() => ({ data: [] })),
      api.get(isExport ? "/exports/orders/" : "/sales-orders/").catch(() => ({ data: [] })),
      api.get(isExport ? "/exports/packing-lists/" : "/packing-lists/").catch(() => ({ data: [] })),
      api.get("/customers/").catch(() => ({ data: [] })),
      api.get("/articles/").catch(() => ({ data: [] })),
    ]);
    // Mar 2, 2026 · Normalise ESO → SO shape so the picker + downstream
    // helpers (onOrderChange, printDc, printInvoice, styleNoForDc) work
    // without branching. Only meaningful in the Exports context.
    const orderRows = isExport
      ? (o.data || []).map(e => ({
          ...e,
          order_no: e.order_no || e.doc_no || "",
          customer_id: e.customer_id || e.buyer_customer_id || "",
          po_number: e.po_number || e.buyer_po_no || "",
          items: (e.items || []).map(li => ({
            ...li,
            rate: (li.rate != null && li.rate !== "") ? li.rate : (li.unit_price_usd || 0),
          })),
        }))
      : o.data;
    // Mar 2, 2026 · Normalise EPL → PL shape so the "Linked Packing Lists"
    // checklist, qty roll-up and DC print all read from the same field
    // names. Boxes stay intact for downstream print/preview logic.
    const plRows = isExport
      ? (p.data || []).map(e => ({
          ...e,
          pl_no: e.pl_no || e.doc_no || "",
          order_id: e.order_id || e.export_order_id || "",
          pack_date: e.pack_date || e.epl_date || "",
          no_of_packages: e.no_of_packages ?? e.total_boxes ?? (e.boxes || []).length,
          quantity: e.quantity ?? e.total_qty ?? (e.boxes || []).reduce((a, b) => a + Number(b.qty || 0), 0),
          // Aug 2026 · EPL header `total_net_weight_kg` / `total_gross_weight_kg`
          // may be null on legacy docs — always fall back to summing the box
          // rows so the DC print shows real weights (was showing 0.000).
          net_weight: e.net_weight ?? (Number(e.total_net_weight_kg) || (e.boxes || []).reduce((a, b) => a + Number(b.net_weight_kg || 0), 0)),
          gross_weight: e.gross_weight ?? (Number(e.total_gross_weight_kg) || (e.boxes || []).reduce((a, b) => a + Number(b.gross_weight_kg || 0), 0)),
          // EPL boxes carry `qty` (meters) + `net_weight_kg` / `gross_weight_kg`;
          // remap so the DC print helpers that expect `meter` / `net_wt` / `gross_wt` just work.
          boxes: (e.boxes || []).map(b => ({
            ...b,
            meter: b.meter ?? b.qty ?? 0,
            net_wt: b.net_wt ?? b.net_weight_kg ?? 0,
            gross_wt: b.gross_wt ?? b.gross_weight_kg ?? 0,
          })),
        }))
      : p.data;
    setRows(d.data); setOrders(dedupById(orderRows)); setPls(plRows); setCustomers(c.data);
    // Feb 2026 · Smart Lock filter — same rationale as SalesOrders.
    setArticles(((a.data) || []).filter(x => x.is_active !== false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const customerName = (id) => customers.find(x => x.id === id)?.name || "";
  // Style Number resolver for the DC preview + print (Feb 2026). Walks the
  // SO's line items looking for a `product_code` match on article_no /
  // item_code and returns the buyer's `style_no`. Same shape as the PL flow
  // so both docs render an identical Style column.
  const styleNoForDc = (order_id, product_code) => {
    if (!order_id || !product_code) return "";
    const so = orders.find(o => o.id === order_id);
    if (!so || !Array.isArray(so.items)) return "";
    const pc = String(product_code).trim().toLowerCase();
    const styles = new Set(so.items.filter(li => {
      const cand = [li.article_no, li.item_code, li.item_no].filter(Boolean).map(s => String(s).trim().toLowerCase());
      return cand.includes(pc);
    }).map(li => String(li.style_no || "").trim()));
    return styles.size === 1 ? [...styles][0] : "";
  };

  // Next-step handoff — if a previous tab queued a SO for us, auto-open the
  // New Delivery Challan dialog with the SO prefilled once orders/PLs load.
  useEffect(() => {
    if (!orders.length) return;
    let orderId = "";
    try { orderId = sessionStorage.getItem("mes_dc_next_order") || ""; } catch (e) { /* noop */ }
    if (!orderId) return;
    try { sessionStorage.removeItem("mes_dc_next_order"); } catch (e) { /* noop */ }
    (async () => {
      await openNew();
      onOrderChange(orderId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, pls]);
  // ── ECI-CANCEL-H01-UAT-02E · Mar 2026 · Owner Rule ─────────────
  // Operational EPL lock is derived ONLY from ACTIVE (non-reversed,
  // non-void, non-DELIVERY_REVERSED) DCs. Historical reversed/void
  // DCs remain visible in the register per UAT-02B (Rule I-12) but
  // their historical `packing_list_ids` MUST NOT disable the EPL in
  // the New EDC picker. Backend `_validate_pls_unique` is the
  // canonical save-side authority and already enforces the same
  // release-boundary; this memo just mirrors that rule for the UI.
  const plUsedInDc = useMemo(() => {
    const m = {};
    rows.filter(r => r.id !== editing?.id).forEach(r => {
      if (r.delivery_reversed === true) return;
      if (r.void === true) return;
      if ((r.status || "").toUpperCase() === "DELIVERY_REVERSED") return;
      const ids = r.packing_list_ids && r.packing_list_ids.length ? r.packing_list_ids : (r.packing_list_id ? [r.packing_list_id] : []);
      ids.forEach(id => { if (id) m[id] = r.dc_no; });
    });
    return m;
  }, [rows, editing]);

  const openNew = () => {
    setEditing(null);
    // Feb 2026 · DC No is auto-assigned on save — mirrors SO / PL flow.
    setForm({ ...empty, dc_no: "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setIsAmending(!!r.delivered);
    setAmendReason("");
    const ids = r.packing_list_ids && r.packing_list_ids.length ? r.packing_list_ids : (r.packing_list_id ? [r.packing_list_id] : []);
    setForm({ ...empty, ...r, packing_list_ids: ids });
    setOpen(true);
  };

  // Auto-pull PLs when SO changes (excluding any already used in another DC)
  const onOrderChange = (order_id) => {
    const o = orders.find(x => x.id === order_id);
    const linked = pls.filter(p => p.order_id === order_id && !plUsedInDc[p.id]);
    const ids = linked.map(p => p.id);
    const qty = linked.reduce((a, p) => a + (Number(p.quantity) || 0), 0);
    const cpo = o?.po_number || (o?.items || []).map(x => x.customer_po_no).find(v => v) || "";
    const cname = customerName(o?.customer_id);
    setForm(f => ({
      ...f,
      order_id,
      packing_list_ids: ids,
      packing_list_id: ids[0] || "",
      quantity: qty,
      customer_name: cname,
      customer_po_no: cpo,
    }));
  };

  const togglePl = (plId) => {
    if (plUsedInDc[plId]) {
      toast.error(`Already dispatched via ${plUsedInDc[plId]}`);
      return;
    }
    const selected = form.packing_list_ids.includes(plId)
      ? form.packing_list_ids.filter(x => x !== plId)
      : [...form.packing_list_ids, plId];
    const qty = pls.filter(p => selected.includes(p.id)).reduce((a, p) => a + (Number(p.quantity) || 0), 0);
    setForm({ ...form, packing_list_ids: selected, packing_list_id: selected[0] || "", quantity: qty });
  };

  const save = async () => {
    if (!form.order_id) return toast.error("Sales order required");
    if (!form.dc_no) {
      // Feb 2026 · Reserve DC No at save-time. Amend path keeps the existing.
      // Aug 2026 · Export DCs reserve from the SEPARATE EDC-YY-YY-
      // series so domestic and export ledgers never collide. Endpoint
      // picked based on the `isExport` prop wired by the parent screen.
      const nextNoUrl = isExport
        ? "/next/export-delivery-challan-no"
        : "/next/delivery-challan-no";
      if (!editing) {
        try {
          const { data: nx } = await api.get(nextNoUrl);
          form.dc_no = nx.dc_no;
        } catch (e) {
          return toast.error("Could not reserve a DC No — try again");
        }
      } else {
        return toast.error("DC No missing on this row");
      }
    }
    try {
      if (editing && isAmending) {
        if (!amendReason.trim() || amendReason.trim().length < 3) {
          return toast.error("Amendment reason is required (min 3 chars)");
        }
        await api.post(`/delivery-challans/${editing.id}/amend`, { reason: amendReason.trim(), updates: form });
        toast.success(`${form.dc_no} amended`);
      } else if (editing) {
        await api.put(`/delivery-challans/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/delivery-challans/", form);
        toast.success(`Challan ${form.dc_no} created`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deliverDc = async (r) => {
    if (!confirm(`Mark ${r.dc_no} as Delivered? Once delivered, edits require an amendment reason.`)) return;
    try {
      await api.post(`/delivery-challans/${r.id}/deliver`, { remarks: "" });
      // Workflow nudge — offer to jump to the Status Report for this SO.
      if (onNextStep && r.order_id) {
        toast.success(`${r.dc_no} marked delivered`, {
          action: {
            label: "→ View Status Report",
            onClick: () => onNextStep("report", r.order_id),
          },
          duration: 8000,
        });
      } else {
        toast.success(`${r.dc_no} marked delivered`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!rbacReady) return;
    const r = rows.find(x => x.id === id);
    if (!canDeleteDc(r)) { toast.error(`🔒 ${deletePermFor(r)} — ask an admin to grant this`); return; }
    if (r?.delivered) return toast.error(`${r.dc_no} is delivered — cannot delete`);
    const reason = window.prompt(`Permanently delete DRAFT ${r?.dc_no || "challan"}?\n\nThis is destructive and recorded in the Audit Log.\nEnter a reason (min 4 characters):`, "");
    if (reason === null) return; // cancelled
    if (reason.trim().length < 4) return toast.error("A delete reason (min 4 characters) is required");
    try {
      await api.delete(`/delivery-challans/${id}`, { data: { reason: reason.trim() } });
      toast.success("Draft challan deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const orderNo = (id) => orders.find(o => o.id === id)?.order_no || "—";
  // Mar 2, 2026 · Scope the DC list to the current context. In Domestic
  // mode `orders` = SOs, in Export mode `orders` = ESOs. A DC created
  // against an SO won't appear in the Exports DC tab and vice-versa,
  // preventing accidental cross-context edits.
  const contextRows = useMemo(() => {
    const ids = new Set(orders.map(o => o.id));
    return rows.filter(r => ids.has(r.order_id));
  }, [rows, orders]);
  const plNo = (id) => pls.find(p => p.id === id)?.pl_no || "—";
  const plNos = (r) => {
    const ids = r.packing_list_ids && r.packing_list_ids.length ? r.packing_list_ids : (r.packing_list_id ? [r.packing_list_id] : []);
    return ids.map(plNo).filter(x => x !== "—").join(", ") || "—";
  };

  const printDc = (r) => {
    const o = orders.find(x => x.id === r.order_id);
    const cname = r.customer_name || customerName(o?.customer_id);
    const cpo = r.customer_po_no || o?.po_number || "";
    const ids = r.packing_list_ids && r.packing_list_ids.length ? r.packing_list_ids : (r.packing_list_id ? [r.packing_list_id] : []);
    const linkedPls = pls.filter(p => ids.includes(p.id));
    const allBoxes = [];
    linkedPls.forEach(pl => (pl.boxes || []).forEach(b => allBoxes.push({ ...b, pl_no: pl.pl_no })));

    // Feb 2026 · DC print switched from box-wise manifest to a Product /
    // Article-wise SUMMARY (boxes · meters · net wt · gross wt) plus the
    // Rate pulled from the Sales Order line so the operator can hand this
    // straight to the invoicing desk. Rate is picked from `so.items` by
    // matching article_no / item_code / item_no — same resolver used for
    // Style No above.
    const rateForArticle = (article_no) => {
      if (!o || !Array.isArray(o.items) || !article_no) return 0;
      const pc = String(article_no).trim().toLowerCase();
      const line = o.items.find(li => {
        const cand = [li.article_no, li.item_code, li.item_no].filter(Boolean).map(s => String(s).trim().toLowerCase());
        return cand.includes(pc);
      });
      return line ? Number(line.rate || 0) : 0;
    };

    const byArticle = new Map();
    allBoxes.forEach(b => {
      const key = String(b.article_no || "—").trim() || "—";
      const cur = byArticle.get(key) || {
        article_no: key,
        style_no: b.style_no || styleNoForDc(r.order_id, key) || "",
        boxes: 0, meters: 0, net_wt: 0, gross_wt: 0,
      };
      cur.boxes += 1;
      cur.meters += Number(b.meter || 0);
      cur.net_wt += Number(b.net_wt || 0);
      cur.gross_wt += Number(b.gross_wt || 0);
      byArticle.set(key, cur);
    });
    // Feb 2026 · HSN look-up joins Article Master by product_code OR
    // article_no so both the composed Product Code and the raw numeric
    // article No resolve to the same HS Code.
    const hsnFor = (code) => {
      const c = String(code || "").trim().toLowerCase();
      if (!c) return "";
      const a = articles.find(x =>
        String(x.product_code || "").trim().toLowerCase() === c ||
        String(x.article_no || "").trim().toLowerCase() === c
      );
      return a?.hs_code || "";
    };
    const summaryRows = Array.from(byArticle.values())
      .map(a => {
        const rate = rateForArticle(a.article_no);
        return { ...a, hs_code: hsnFor(a.article_no), rate, value: a.meters * rate };
      })
      .sort((a, b) => a.article_no.localeCompare(b.article_no));
    const totalValue = summaryRows.reduce((s, x) => s + (x.value || 0), 0);

    const sections = [
      {
        heading: `Linked Packing Lists · ${linkedPls.length}`,
        columns: [
          { key: "pl_no", label: "PL No", mono: true },
          { key: "pack_date", label: "Date", mono: true },
          { key: "no_of_packages", label: "Boxes", align: "right" },
          { key: "quantity", label: "Qty (m)", align: "right", fmt: v => fmtQty(v, "MTR") },
          { key: "net_weight", label: "Net Wt (kg)", align: "right", fmt: v => fmtQty(v, "KG") },
          { key: "gross_weight", label: "Gross Wt (kg)", align: "right", fmt: v => fmtQty(v, "KG") },
        ],
        rows: linkedPls,
        footerRow: {
          pl_no: `Total ${linkedPls.length} PL`,
          no_of_packages: linkedPls.reduce((a, p) => a + (Number(p.no_of_packages) || 0), 0),
          quantity: fmtQty(linkedPls.reduce((a, p) => a + (Number(p.quantity) || 0), 0), "MTR"),
          net_weight: fmtQty(linkedPls.reduce((a, p) => a + (Number(p.net_weight) || 0), 0), "KG"),
          gross_weight: fmtQty(linkedPls.reduce((a, p) => a + (Number(p.gross_weight) || 0), 0), "KG"),
        },
      },
    ];
    if (summaryRows.length > 0) {
      // Aug 2026 · Export DCs (EDC-*) price lines in USD from the ESO's
      // unit_price_usd — flip Rate / Value column labels + formatting to $
      // so the printed DC lines up with the Commercial Invoice.
      const isExportDc = String(r.dc_no || "").toUpperCase().startsWith("EDC-") || isExport;
      const rateLabel  = isExportDc ? "Rate ($)"  : "Rate (₹)";
      const valueLabel = isExportDc ? "Value ($)" : "Value (₹)";
      const fmtMoney = (v) => isExportDc
        ? `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : fmtInr(v || 0, { symbol: false });
      sections.push({
        heading: `Product / Article Summary · ${summaryRows.length} article${summaryRows.length !== 1 ? "s" : ""}`,
        columns: [
          { key: "article_no", label: "Product Code", mono: true },
          { key: "style_no", label: "Style No", mono: true, fmt: v => v || "—" },
          { key: "hs_code", label: "HSN", mono: true, fmt: v => v || "—" },
          { key: "boxes", label: "Boxes", align: "right" },
          { key: "meters", label: "Total Meters", align: "right", fmt: v => fmtQty(v, "MTR") },
          { key: "net_wt", label: "Net Wt (kg)", align: "right", fmt: v => fmtQty(v, "KG") },
          { key: "gross_wt", label: "Gross Wt (kg)", align: "right", fmt: v => fmtQty(v, "KG") },
          { key: "rate", label: rateLabel, align: "right", fmt: fmtMoney },
          { key: "value", label: valueLabel, align: "right", fmt: fmtMoney },
        ],
        rows: summaryRows,
        footerRow: {
          article_no: `Total ${summaryRows.length} article${summaryRows.length !== 1 ? "s" : ""}`,
          boxes: summaryRows.reduce((a, x) => a + (Number(x.boxes) || 0), 0),
          meters: fmtQty(summaryRows.reduce((a, x) => a + (Number(x.meters) || 0), 0), "MTR"),
          net_wt: fmtQty(summaryRows.reduce((a, x) => a + (Number(x.net_wt) || 0), 0), "KG"),
          gross_wt: fmtQty(summaryRows.reduce((a, x) => a + (Number(x.gross_wt) || 0), 0), "KG"),
          rate: "",
          value: fmtMoney(totalValue),
        },
      });
    }
    printSignedDocument(String(r.dc_no || "").toUpperCase().startsWith("EDC-") ? "EDC" : "DC", r.id, {
      kind: r.delivered ? "Delivery Challan (Delivered)" : "Delivery Challan",
      no: r.dc_no,
      date: r.challan_date,
      meta: [
        { label: "Customer Name", value: cname || "—" },
        { label: "Customer PO No", value: cpo || "—" },
        { label: "Sales Order", value: orderNo(r.order_id) },
        { label: "Vehicle No", value: r.vehicle_no || "—" },
        { label: "LR No", value: r.lr_no || "—" },
        { label: "Driver", value: r.driver_name || "—" },
        { label: "Transporter", value: r.transporter || "—" },
        { label: "Total Qty (m)", value: fmtQty(r.quantity, "MTR") },
      ],
      sections,
      // Mar 2026 · DC/EDC footer — 4-column signature row per operator
      // brief. "Authorised By" auto-prints the admin who marked the DC
      // Delivered (server writes `delivered_by`) or Authorised
      // (`authorised_by`), whichever exists. Blank line remains when
      // neither is set so a manual signature is still possible.
      signatures: [
        "Prepared By",
        "Driver Signature",
        "Receiver's Signature",
        {
          label: "Authorised By",
          name: r.delivered_by || r.authorised_by || r.approved_by || "",
          // Mar 2026 · Signature Timestamp — print the ISO-time of
          // authorisation under the signature line for audit clarity
          // (only rendered when a name is present).
          timestamp: (r.delivered_by || r.authorised_by || r.approved_by)
            ? (fmtDateTimeIST(r.delivered_at || r.authorised_at || r.approved_at) || "")
            : "",
        },
      ],
      footer: r.remarks ? `Remarks: ${r.remarks}` : "",
    });
  };

  // Feb 2026 · One-click Tax Invoice from a DC. Reuses the same
  // article-summary math as `printDc`, then layers GST split on top:
  //  - Intra-state (customer's Bill-To state matches COMPANY_STATE)
  //    → CGST + SGST, each at half the applicable GST rate
  //  - Inter-state (any other Indian state) → IGST at the full rate
  //  - International / no state → treated as inter-state (IGST)
  // GST rate defaults to 5% (standard on narrow-fabric HS chapters). If a
  // future finance rule changes this, `DEFAULT_GST_RATE_PCT` is the single
  // knob to twist.
  const COMPANY_STATE = "Gujarat";
  const COMPANY_STATE_ALIASES = new Set(["gujarat", "gj"]);
  const DEFAULT_GST_RATE_PCT = 5;

  const printInvoice = (r) => {
    const o = orders.find(x => x.id === r.order_id);
    const cust = customers.find(c => c.id === o?.customer_id);
    const billTo = (cust?.addresses || []).find(a => a.is_primary_bill) || (cust?.addresses || [])[0] || null;
    const shipTo = (cust?.addresses || []).find(a => a.is_primary_ship) || billTo;
    const cpo = r.customer_po_no || o?.po_number || "";
    const ids = r.packing_list_ids && r.packing_list_ids.length ? r.packing_list_ids : (r.packing_list_id ? [r.packing_list_id] : []);
    const linkedPls = pls.filter(p => ids.includes(p.id));
    const allBoxes = [];
    linkedPls.forEach(pl => (pl.boxes || []).forEach(b => allBoxes.push({ ...b, pl_no: pl.pl_no })));

    // Aggregate by article + join Rate (from SO line) and HSN (from Article Master)
    const rateForArticle = (article_no) => {
      if (!o || !Array.isArray(o.items) || !article_no) return 0;
      const pc = String(article_no).trim().toLowerCase();
      const line = o.items.find(li => {
        const cand = [li.article_no, li.item_code, li.item_no].filter(Boolean).map(s => String(s).trim().toLowerCase());
        return cand.includes(pc);
      });
      return line ? Number(line.rate || 0) : 0;
    };
    const hsnFor = (code) => {
      const c = String(code || "").trim().toLowerCase();
      if (!c) return "";
      const a = articles.find(x =>
        String(x.product_code || "").trim().toLowerCase() === c ||
        String(x.article_no || "").trim().toLowerCase() === c
      );
      return a?.hs_code || "";
    };
    const byArticle = new Map();
    allBoxes.forEach(b => {
      const key = String(b.article_no || "—").trim() || "—";
      const cur = byArticle.get(key) || { article_no: key, boxes: 0, meters: 0 };
      cur.boxes += 1;
      cur.meters += Number(b.meter || 0);
      byArticle.set(key, cur);
    });

    // Intra vs inter-state → CGST+SGST vs IGST
    const custState = (billTo?.country || "").toLowerCase() === "india" || !billTo?.country
      ? (billTo?.state || "").toLowerCase()
      : "__international__";
    const isIntraState = COMPANY_STATE_ALIASES.has(custState);
    const gstRate = DEFAULT_GST_RATE_PCT;
    const halfRate = gstRate / 2;

    const invoiceRows = Array.from(byArticle.values())
      .map(a => {
        const rate = rateForArticle(a.article_no);
        const taxable = a.meters * rate;
        const cgst = isIntraState ? taxable * halfRate / 100 : 0;
        const sgst = isIntraState ? taxable * halfRate / 100 : 0;
        const igst = isIntraState ? 0 : taxable * gstRate / 100;
        return {
          article_no: a.article_no,
          hs_code: hsnFor(a.article_no),
          meters: a.meters,
          rate,
          taxable,
          cgst, sgst, igst,
          total: taxable + cgst + sgst + igst,
        };
      })
      .sort((a, b) => a.article_no.localeCompare(b.article_no));

    const sum = (k) => invoiceRows.reduce((s, x) => s + (Number(x[k]) || 0), 0);
    const totals = {
      taxable: sum("taxable"),
      cgst: sum("cgst"),
      sgst: sum("sgst"),
      igst: sum("igst"),
      grand: sum("total"),
    };

    // Format a multi-line Bill-To / Ship-To block for the invoice header meta.
    const addressLine = (a) => {
      if (!a) return "—";
      const parts = [a.address, [a.city, a.state, a.pincode].filter(Boolean).join(", "), a.country].filter(Boolean);
      const bits = [parts.join(", ")];
      if (a.gstin) bits.push(`GSTIN: ${a.gstin}`);
      return bits.join(" · ");
    };

    const invoiceCols = [
      { key: "article_no", label: "Product Code", mono: true },
      { key: "hs_code", label: "HSN", mono: true, fmt: v => v || "—" },
      { key: "meters", label: "Qty (m)", align: "right", fmt: v => fmtQty(v, "MTR") },
      { key: "rate", label: "Rate (₹/m)", align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
      { key: "taxable", label: "Taxable Value (₹)", align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
      ...(isIntraState
        ? [
            { key: "cgst", label: `CGST @ ${halfRate}% (₹)`, align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
            { key: "sgst", label: `SGST @ ${halfRate}% (₹)`, align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
          ]
        : [
            { key: "igst", label: `IGST @ ${gstRate}% (₹)`, align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
          ]),
      { key: "total", label: "Total (₹)", align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
    ];

    const invoiceFooter = {
      article_no: `Grand Total · ${invoiceRows.length} line${invoiceRows.length !== 1 ? "s" : ""}`,
      meters: fmtQty(invoiceRows.reduce((s, x) => s + Number(x.meters || 0), 0), "MTR"),
      taxable: fmtInr(totals.taxable, { symbol: false }),
      cgst: fmtInr(totals.cgst, { symbol: false }),
      sgst: fmtInr(totals.sgst, { symbol: false }),
      igst: fmtInr(totals.igst, { symbol: false }),
      total: fmtInr(totals.grand, { symbol: false }),
    };

    printDocument({
      kind: "Tax Invoice",
      no: `INV-${r.dc_no}`,
      meta: [
        { label: "DC Reference", value: r.dc_no },
        { label: "Invoice Date", value: r.challan_date || new Date().toISOString().slice(0, 10) },
        { label: "Customer", value: cust?.name || r.customer_name || "—" },
        { label: "Customer PO", value: cpo || "—" },
        { label: "Bill To", value: addressLine(billTo) },
        { label: "Ship To", value: addressLine(shipTo) },
        { label: "Place of Supply", value: (billTo?.state || "—") + (billTo?.country ? ` · ${billTo.country}` : "") },
        { label: "Tax Treatment", value: isIntraState ? `Intra-state · CGST + SGST @ ${gstRate}%` : `Inter-state · IGST @ ${gstRate}%` },
      ],
      sections: [
        {
          heading: `Invoice Lines · ${invoiceRows.length} article${invoiceRows.length !== 1 ? "s" : ""}`,
          columns: invoiceCols,
          rows: invoiceRows,
          footerRow: invoiceFooter,
        },
        {
          heading: "Tax Summary",
          columns: [
            { key: "label", label: "Component" },
            { key: "amount", label: "Amount (₹)", align: "right", fmt: v => fmtInr(v || 0, { symbol: false }) },
          ],
          rows: [
            { label: "Taxable Value", amount: totals.taxable },
            ...(isIntraState
              ? [
                  { label: `CGST @ ${halfRate}%`, amount: totals.cgst },
                  { label: `SGST @ ${halfRate}%`, amount: totals.sgst },
                ]
              : [
                  { label: `IGST @ ${gstRate}%`, amount: totals.igst },
                ]),
            { label: "Grand Total (Invoice Value)", amount: totals.grand },
          ],
        },
      ],
      signatures: ["Prepared By", "Verified By", "For " + (cust?.name || "Customer")],
      footer: "Certified that the particulars given above are true and correct. E. & O.E.",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 mr-auto">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filter</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" data-testid="dc-status-filter">
            {[
              { k: "all", label: "All", count: contextRows.length },
              { k: "draft", label: "Draft", count: contextRows.filter(r => !r.delivered).length },
              { k: "delivered", label: "Delivered", count: contextRows.filter(r => !!r.delivered).length },
            ].map(t => (
              <button
                key={t.k}
                data-testid={`dc-filter-${t.k}`}
                onClick={() => setStatusFilter(t.k)}
                className={`px-3 h-8 text-xs font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${statusFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
              >
                {t.label}
                <span className={`ml-1.5 font-mono text-[10px] ${statusFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">Sort</div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="dc-sort" className="bg-[var(--bg-0)] border-[var(--border)] h-8 text-xs w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
              <SelectItem value="date_desc">Date · newest first</SelectItem>
              <SelectItem value="date_asc">Date · oldest first</SelectItem>
              <SelectItem value="dc_asc">DC No · A → Z</SelectItem>
              <SelectItem value="dc_desc">DC No · Z → A</SelectItem>
              <SelectItem value="qty_desc">Quantity · high → low</SelectItem>
              <SelectItem value="vehicle_asc">Vehicle · A → Z</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative ml-2 w-72">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="dc-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="DC No / SO / Vehicle / LR / Driver…"
              className="w-full h-8 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          {search && (
            <button data-testid="dc-search-clear" onClick={() => setSearch("")} className="h-8 px-2 text-[11px] text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
          )}
        </div>
        <button data-testid="new-dc-btn" onClick={openNew} disabled={!rbacReady || !canCreateDc} title={rbacReady && !canCreateDc ? "🔒 sales_orders_challans.create — ask an admin to grant this" : undefined} className={`h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2 ${(!rbacReady || !canCreateDc) ? "opacity-50 cursor-not-allowed" : ""}`}>
          <PlusIcon size={14} weight="bold" />{isExport ? "New EDC" : "New Delivery Challan"}
        </button>
        <button
          onClick={() => exportToCSV("delivery-challans.csv", contextRows.map(r => ({ ...r, order_no: orderNo(r.order_id), pl_no: plNos(r) })), [
            { key: "dc_no", label: "DC No" }, { key: "order_no", label: "Sales Order" }, { key: "pl_no", label: "Packing List" },
            { key: "challan_date", label: "Date" }, { key: "vehicle_no", label: "Vehicle" },
            { key: "lr_no", label: "LR No" },
            { key: "driver_name", label: "Driver" }, { key: "transporter", label: "Transporter" }, { key: "quantity", label: "Qty" },
          ])}
          className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"
        >
          <DownloadSimpleIcon size={14} weight="bold" />Export CSV
        </button>
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">DC No</th>
              <th className="text-left px-4 py-3">DC Date</th>
              <th className="text-left px-4 py-3">SO No</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">PO No</th>
              <th className="text-left px-4 py-3">Packing List</th>
              {/* A2 · Phase 2 (Oct 2026) — Export EDC Boxes column.
                  A2-D01 · Nov 2026 — Extended to Domestic DC. Backend
                  `/delivery-challans/` stamps `box_count` on BOTH
                  Export EDCs (from `export_packing_lists.boxes[]`) and
                  Domestic DCs (from `packing_lists.no_of_packages`).
                  Owner A2-D01 §4 — Boxes between Packing List and
                  Status for all DCs. */}
              <th className="text-right px-4 py-3" data-testid="dc-th-boxes">Boxes</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Vehicle</th>
              <th className="text-left px-4 py-3">LR No</th>
              <th className="text-left px-4 py-3">Driver</th>
              <th className="text-left px-4 py-3">Transporter</th>
              <th className="text-right px-4 py-3">Qty (m)</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const q = search.trim().toLowerCase();
              const filtered = contextRows.filter(r => {
                if (statusFilter === "draft" && r.delivered) return false;
                if (statusFilter === "delivered" && !r.delivered) return false;
                if (q) {
                  const so = orderNo(r.order_id) || "";
                  const hay = `${r.dc_no} ${so} ${r.vehicle_no || ""} ${r.lr_no || ""} ${r.driver_name || ""} ${r.transporter || ""} ${plNos(r)}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              const sorted = [...filtered].sort((a, b) => {
                switch (sortBy) {
                  case "date_asc": return (a.challan_date || "").localeCompare(b.challan_date || "");
                  case "date_desc": return (b.challan_date || "").localeCompare(a.challan_date || "");
                  case "dc_asc": return (a.dc_no || "").localeCompare(b.dc_no || "");
                  case "dc_desc": return (b.dc_no || "").localeCompare(a.dc_no || "");
                  case "qty_desc": return (Number(b.quantity) || 0) - (Number(a.quantity) || 0);
                  case "vehicle_asc": return (a.vehicle_no || "").localeCompare(b.vehicle_no || "");
                  default: return 0;
                }
              });
              return sorted.map(r => {
              // Feb 2026 · Show Customer + PO No so shipping/dispatch
              // teams can validate against the SO without switching tabs.
              const _o = orders.find(o => o.id === r.order_id);
              const _cname = r.customer_name || customerName(_o?.customer_id) || "—";
              const _cpo   = r.customer_po_no || (_o?.po_number) || "—";
              return (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`dc-${r.dc_no}`}>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.dc_no} className="text-white" /></td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.challan_date}</td>
                <td className="px-4 py-3 font-mono">{orderNo(r.order_id)}</td>
                <td className="px-4 py-3" data-testid={`dc-customer-${r.dc_no}`}>{_cname}</td>
                <td className="px-4 py-3 font-mono text-zinc-400" data-testid={`dc-po-${r.dc_no}`}>{_cpo}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{plNos(r)}</td>
                {/* A2 · Phase 2 (Oct 2026) · A2-D01 · Nov 2026 — Boxes
                    cell for Export + Domestic DC. Reads the backend-
                    aggregated `r.box_count` stamped by
                    `/delivery-challans/` (Export: EPL `boxes[]` length;
                    Domestic: PL `no_of_packages`). `null` ONLY when
                    linked refs exist but none resolve → render dash.
                    An absent `box_count` (DC with no linked PL at all)
                    also renders dash. */}
                <td className="px-4 py-3 text-right font-mono text-white" data-testid={`dc-boxes-${r.dc_no}`}>
                  {r.box_count != null ? r.box_count : "—"}
                </td>
                <td className="px-4 py-3" data-testid={`dc-status-${r.dc_no}`}>
                  {(r.delivery_reversed || (r.status || "").toUpperCase() === "DELIVERY_REVERSED") ? (
                    /* ── ECI-CANCEL-H01-UAT-02B (Mar 2026) · Historical
                         REVERSED badge. Owner Rule I-12 — a reversed EDC
                         is a permanent auditable historical business
                         document; it stays visible in the register (see
                         backend `list_dcs` OR-branch) with a distinct
                         badge and audit tooltip. It is NOT operationally
                         eligible for further actions (no Reverse, no
                         Delete). */
                    <span
                      data-testid={`dc-reversed-${r.dc_no}`}
                      title={r.delivery_reversal_reason
                        ? `Reversed by ${r.delivery_reversed_by || "—"} on ${fmtDateTimeIST(r.delivery_reversed_at) || "—"} · Reason: ${r.delivery_reversal_reason}`
                        : "Historical delivery reversal · audit preserved"}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-rose-500/10 text-rose-400 text-[11px] font-mono uppercase tracking-widest">
                      <ArrowCounterClockwiseIcon size={10} weight="bold" />Reversed
                    </span>
                  ) : r.delivered ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 text-[11px] font-mono uppercase tracking-widest">
                      <LockIcon size={10} weight="bold" />Delivered
                      {(r.amendments?.length || 0) > 0 && (
                        <button onClick={() => setHistoryDc(r)} data-testid={`history-dc-${r.dc_no}`} title={`${r.amendments.length} amendment(s)`} className="inline-flex items-center gap-0.5 ml-1 text-amber-400 hover:underline">
                          <ClockCounterClockwiseIcon size={11} />
                          <span>{r.amendments.length} amend</span>
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Draft
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono">{r.vehicle_no}</td>
                <td className="px-4 py-3 font-mono text-zinc-300" data-testid={`dc-lr-${r.dc_no}`}>
                  {r.lr_no ? r.lr_no : (
                    r.delivered ? (
                      <span
                        data-testid={`dc-lr-missing-${r.dc_no}`}
                        className="inline-flex items-center gap-1 text-amber-400"
                        title="Delivered without an LR number — freight reconciliation pending. Amend the DC to back-fill."
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-[11px] uppercase tracking-widest">LR pending</span>
                      </span>
                    ) : <span className="text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-4 py-3">{r.driver_name}</td>
                <td className="px-4 py-3 text-zinc-400">{r.transporter}</td>
                <td className="px-4 py-3 text-right font-mono">{Number(r.quantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                <td className="px-4 py-3 text-right">
                  <RowActionsMenu testId={`dc-actions-${r.dc_no}`}>
                  <button onClick={() => setPreview(r)} data-testid={`view-dc-${r.dc_no}`} title="View DC" className="px-2 text-xs text-[var(--accent)]">View</button>
                  <button onClick={() => printDc(r)} data-testid={`print-dc-${r.dc_no}`} title="Print / PDF" className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-[var(--accent)]"><PrinterIcon size={14} /></button>
                  <DocDownloadIcons type={String(r.dc_no || "").toUpperCase().startsWith("EDC-") ? "EDC" : "DC"} id={r.id} testPrefix={`dc-row-${r.dc_no}`} />
                  {!isReversedDc(r) && (
                    <button onClick={() => printInvoice(r)} data-testid={`invoice-dc-${r.dc_no}`} title="Generate Tax Invoice (GST split)" className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><ReceiptIcon size={14} /></button>
                  )}
                  {!r.delivered && (
                    <button onClick={() => deliverDc(r)} data-testid={`deliver-dc-${r.dc_no}`} title="Mark Delivered" className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><CheckCircleIcon size={14} weight="bold" /></button>
                  )}
                  {!isReversedDc(r) && (
                    <button data-testid={`edit-dc-${r.dc_no}`} onClick={() => openEdit(r)} disabled={!rbacReady || !canEditDc} title={rbacReady && !canEditDc ? "🔒 sales_orders_challans.edit — ask an admin to grant this" : (r.delivered ? "Amend" : "Edit")} className={`p-1.5 rounded-sm hover:bg-[var(--bg-2)] ${(!rbacReady || !canEditDc) ? "opacity-40 cursor-not-allowed" : ""}`}><PencilIcon size={14} /></button>
                  )}
                  {/* REL-02 · mirrors reverse-entry: delivered, not reversed/void, domestic only (ECI-FIN-H01 refuses EDC). */}
                  {r.delivered && !isReversedDc(r) && (r.status || "").toLowerCase() !== "voided" && !isEdcRow(r) && !r.is_export && rbacReady && rbacCan("sales_orders_challans", "reverse") && (
                    <button
                      onClick={async () => {
                        const reason = window.prompt(
                          `Reverse DC ${r.dc_no || ""}?\n\nPosts equal-and-opposite stock movements, restores FG lot balances, and flags the challan Voided (audit trail preserved).\n\nReason (min 4 chars):`, "",
                        );
                        if (reason === null) return;
                        if ((reason || "").trim().length < 4) { toast.error("Reversal reason must be at least 4 characters"); return; }
                        try {
                          const { data } = await api.post(`/delivery-challans/${r.id}/reverse-entry`, { reason: reason.trim() });
                          toast.success(`${data.dc_no || r.dc_no} · ${data.reversal_legs} leg(s) reversed`);
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Reversal failed"); }
                      }}
                      data-testid={`reverse-dc-${r.dc_no}`}
                      title="Reverse this challan (audit-preserving · DC flagged Voided)"
                      className="p-1.5 rounded-sm border border-amber-500/40 hover:bg-amber-500/10 text-amber-300">
                      <ArrowCounterClockwiseIcon size={14} weight="bold" />
                    </button>
                  )}
                  {!r.delivered && (
                    <button data-testid={`delete-dc-${r.dc_no}`} onClick={() => remove(r.id)} disabled={!rbacReady || !canDeleteDc(r)} title={rbacReady && !canDeleteDc(r) ? `🔒 ${deletePermFor(r)} — ask an admin to grant this` : "Permanently delete DC (destructive · no audit trail)"} className={`p-1.5 rounded-sm hover:bg-red-500/10 text-red-400 ${(!rbacReady || !canDeleteDc(r)) ? "opacity-40 cursor-not-allowed" : ""}`}><TrashIcon size={14} /></button>
                  )}
                  </RowActionsMenu>
                </td>
              </tr>
              );
              });
            })()}
            {contextRows.length === 0 && <tr><td colSpan={12} className="text-center py-14 text-zinc-500">No delivery challans yet.</td></tr>}
            {contextRows.length > 0 && (() => {
              const q = search.trim().toLowerCase();
              const visible = contextRows.filter(r => {
                if (statusFilter === "draft" && r.delivered) return false;
                if (statusFilter === "delivered" && !r.delivered) return false;
                if (q) {
                  const so = orderNo(r.order_id) || "";
                  const hay = `${r.dc_no} ${so} ${r.vehicle_no || ""} ${r.lr_no || ""} ${r.driver_name || ""} ${r.transporter || ""} ${plNos(r)}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              return visible.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-zinc-500">No delivery challans match the current filter{q ? ` / search "${search}"` : ""}.</td></tr>
              );
            })()}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl max-h-[92vh] overflow-y-auto" description="Manage extra-quantity approvals, cancel-balance and short-close requests on this order.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>{editing ? (isAmending ? "Amend" : "Edit") : "New"} {isExport ? "EDC" : "Delivery Challan"}</span>
              {isAmending && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono bg-emerald-500/10 text-emerald-400">
                  <LockIcon size={10} weight="bold" />DELIVERED
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {isAmending && (
            <div className="mb-3 p-3 rounded-sm border border-amber-500/30 bg-amber-500/5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400 mb-1">Amendment Reason (min 3 chars) *</div>
              <input
                data-testid="dc-amend-reason"
                value={amendReason}
                onChange={e => setAmendReason(e.target.value)}
                placeholder="e.g. Vehicle number correction, driver change, quantity adjustment"
                className={inp + " border-amber-500/30 focus:border-amber-400"}
              />
            </div>
          )}

          {(form.customer_name || form.customer_po_no) && (
            <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3" data-testid="dc-customer-header">
              <div>
                <div className="overline text-[10px] mb-0.5">Customer Name</div>
                <div className="font-semibold text-sm" data-testid="dc-customer-name">{form.customer_name || "—"}</div>
              </div>
              <div>
                <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                <div className="font-mono text-sm" data-testid="dc-customer-po">{form.customer_po_no || "—"}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <F label="DC No (auto)"><input
              data-testid="dc-no"
              value={form.dc_no}
              readOnly
              placeholder="Auto · assigned on save"
              className={inp + " opacity-70 cursor-not-allowed bg-[var(--bg-2)]/60 font-mono"}
              title="Auto-assigned on save — mirrors SO / PL behaviour. Cancel this draft without leaving a gap in the sequence."
            /></F>
            <F label="Date"><input type="date" value={form.challan_date} onChange={e => setForm({ ...form, challan_date: e.target.value })} className={inp} /></F>
            <F label="Sales Order" className="col-span-2">
              <Select value={form.order_id} onValueChange={onOrderChange}>
                <SelectTrigger data-testid="dc-order" className={selCls}><SelectValue placeholder="Select order" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {orders.filter(o => o.order_against !== "stock").map(o => <SelectItem key={o.id} value={o.id}>{o.order_no}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>

          {/* Auto-pulled Packing Lists */}
          {form.order_id && (
            <div className="mt-3">
              <div className="overline mb-2 flex items-center justify-between">
                <span>Linked Packing Lists</span>
                <span className="text-[10px] text-zinc-500">Auto-pulled from Sales Order · tick / untick to include</span>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-hidden" data-testid="dc-linked-pls">
                {pls.filter(p => p.order_id === form.order_id).length === 0 && (
                  <div className="px-4 py-6 text-center text-xs text-zinc-500">No packing lists exist for this Sales Order yet. Create one in the Packing Lists tab first.</div>
                )}
                {pls.filter(p => p.order_id === form.order_id).map(p => {
                  const checked = form.packing_list_ids.includes(p.id);
                  const usedIn = plUsedInDc[p.id];
                  const disabled = !!usedIn;
                  return (
                    <label key={p.id} className={`flex items-center gap-3 px-3 py-2 border-b border-[var(--border)] text-sm ${disabled ? "opacity-50 cursor-not-allowed bg-[var(--bg-2)]/30" : checked ? "bg-[var(--accent)]/10 cursor-pointer" : "hover:bg-[var(--bg-2)] cursor-pointer"}`} data-testid={`dc-pl-${p.pl_no}`}>
                      <input type="checkbox" checked={checked && !disabled} disabled={disabled} onChange={() => togglePl(p.id)} className="accent-[var(--accent)]" />
                      <span className="font-mono w-32">{p.pl_no}</span>
                      <span className="text-zinc-400 text-xs">{p.pack_date}</span>
                      {disabled && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 font-mono" data-testid={`pl-used-${p.pl_no}`}>
                          Dispatched in {usedIn}
                        </span>
                      )}
                      <span className="ml-auto font-mono text-xs">{p.no_of_packages} boxes</span>
                      <span className="font-mono w-24 text-right">{Number(p.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })} m</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-3">
            <F label="Vehicle No"><input data-testid="dc-vehicle-no" value={form.vehicle_no} onChange={e => setForm({ ...form, vehicle_no: e.target.value.toUpperCase() })} className={inp} placeholder="E.G. MH-12-AB-1234" style={{ textTransform: "uppercase" }} /></F>
            <F label="Quantity (m) · auto from PLs"><FormattedNumberInput data-testid="dc-qty" value={form.quantity} readOnly title="Quantity is auto-calculated from the linked Packing Lists" className={inp + " cursor-not-allowed opacity-70 bg-[var(--bg-2)]/60"} /></F>
            <F label="LR No (Lorry Receipt)"><input data-testid="dc-lr-no" value={form.lr_no} onChange={e => setForm({ ...form, lr_no: e.target.value.toUpperCase() })} className={inp} placeholder="E.G. LR-2026-00187" style={{ textTransform: "uppercase" }} /></F>
            <F label="Driver Name"><input data-testid="dc-driver-name" value={form.driver_name} onChange={e => setForm({ ...form, driver_name: e.target.value.toUpperCase() })} className={inp} style={{ textTransform: "uppercase" }} /></F>
            <F label="Transporter" className="col-span-2"><input value={form.transporter} onChange={e => setForm({ ...form, transporter: e.target.value })} className={inp} /></F>
            <F label="Remarks" className="col-span-2"><input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-dc" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">{isAmending ? "Submit Amendment" : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[85vh] overflow-y-auto" description="Manage extra-quantity approvals, cancel-balance and short-close requests on this order.">
          <DialogHeader><DialogTitle className="font-display">Delivery Challan — {preview?.dc_no}</DialogTitle></DialogHeader>
          {preview && (() => {
            const o = orders.find(x => x.id === preview.order_id);
            const cname = preview.customer_name || customerName(o?.customer_id);
            const cpo = preview.customer_po_no || o?.po_number || "";
            const ids = preview.packing_list_ids && preview.packing_list_ids.length ? preview.packing_list_ids : (preview.packing_list_id ? [preview.packing_list_id] : []);
            const linkedPls = pls.filter(p => ids.includes(p.id));
            const allBoxes = [];
            linkedPls.forEach(pl => (pl.boxes || []).forEach(b => allBoxes.push({ ...b, pl_no: pl.pl_no })));
            const totalBoxes = linkedPls.reduce((a, p) => a + (Number(p.no_of_packages) || 0), 0);
            return (
              <>
                <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/60 px-4 py-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer Name</div>
                    <div className="font-semibold text-sm">{cname || "—"}</div>
                  </div>
                  <div>
                    <div className="overline text-[10px] mb-0.5">Customer PO No</div>
                    <div className="font-mono text-sm">{cpo || "—"}</div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 text-xs mb-3">
                  <div><div className="overline text-[10px] mb-0.5">Sales Order</div><div className="font-mono">{orderNo(preview.order_id)}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Date</div><div className="font-mono">{preview.challan_date}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Vehicle No</div><div className="font-mono">{preview.vehicle_no || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">LR No</div><div className="font-mono">{preview.lr_no || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Qty (m)</div><div className="font-mono font-semibold">{Number(preview.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Driver</div><div>{preview.driver_name || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Transporter</div><div>{preview.transporter || "—"}</div></div>
                  <div><div className="overline text-[10px] mb-0.5">Total Boxes</div><div className="font-mono">{totalBoxes}</div></div>
                </div>

                <DocumentInfo docNo={preview.dc_no} createdAt={preview.created_at} updatedAt={preview.updated_at} createdBy={preview.created_by} docLabel="Delivery Challan No." testidPrefix="dc" />

                <div className="overline mb-2">Linked Packing Lists</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="px-2 py-2 text-left">PL No</th>
                        <th className="px-2 py-2 text-left">Date</th>
                        <th className="px-2 py-2 text-right">Boxes</th>
                        <th className="px-2 py-2 text-right">Qty (m)</th>
                        <th className="px-2 py-2 text-right">Net Wt</th>
                        <th className="px-2 py-2 text-right">Gross Wt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedPls.map(p => (
                        <tr key={p.id} className="border-t border-[var(--border)]">
                          <td className="px-2 py-1 font-mono font-semibold">{p.pl_no}</td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{p.pack_date}</td>
                          <td className="px-2 py-1 text-right font-mono">{p.no_of_packages}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.net_weight).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(p.gross_weight).toFixed(2)}</td>
                        </tr>
                      ))}
                      {linkedPls.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-zinc-500">No packing lists linked.</td></tr>}
                    </tbody>
                  </table>
                </div>

                {allBoxes.length > 0 && (
                  <div className="mt-4">
                    <div className="overline mb-2">Box Manifest · {allBoxes.length} Boxes</div>
                    <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-[var(--bg-2)]">
                          <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                            <th className="px-2 py-2 text-left">Box No</th>
                            <th className="px-2 py-2 text-left">PL</th>
                            <th className="px-2 py-2 text-left">Product Code</th>
                            <th className="px-2 py-2 text-left">Style No</th>
                            <th className="px-2 py-2 text-right">Meter</th>
                            <th className="px-2 py-2 text-right">Net</th>
                            <th className="px-2 py-2 text-right">Gross</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allBoxes.map(b => (
                            <tr key={`${b.pl_no}-${b.box_no}`} className="border-t border-[var(--border)]">
                              <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                              <td className="px-2 py-1 font-mono text-zinc-400">{b.pl_no}</td>
                              <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || "—"}</td>
                              <td className="px-2 py-1 font-mono text-[11px] text-emerald-300">{b.style_no || styleNoForDc(preview.order_id, b.article_no) || <span className="text-zinc-600">—</span>}</td>
                              <td className="px-2 py-1 text-right font-mono">{Number(b.meter).toFixed(2)}</td>
                              <td className="px-2 py-1 text-right font-mono">{Number(b.net_wt).toFixed(2)}</td>
                              <td className="px-2 py-1 text-right font-mono">{Number(b.gross_wt).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {preview.remarks && (
                  <div className="mt-3 text-xs"><span className="overline">Remarks:</span> <span className="text-zinc-400">{preview.remarks}</span></div>
                )}
              </>
            );
          })()}
          <DialogFooter>
            <button onClick={() => preview && printDc(preview)} data-testid="preview-print-dc" className="px-4 py-2 rounded-sm border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print / PDF</button>
            {preview?.id && <DocDownloadButtons type={String(preview.dc_no || "").toUpperCase().startsWith("EDC-") ? "EDC" : "DC"} id={preview.id} testPrefix="dc-preview" />}
            <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Amendment History Dialog */}
      <Dialog open={!!historyDc} onOpenChange={(o) => !o && setHistoryDc(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dc-history-dialog" description="Manage extra-quantity approvals, cancel-balance and short-close requests on this order.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClockCounterClockwiseIcon size={18} weight="bold" />
              Amendment History — {historyDc?.dc_no}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {historyDc?.delivered && (
              <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <LockIcon size={14} weight="bold" className="text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">Delivered</span>
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  by <span className="font-mono">{historyDc.delivered_by}</span> on <span className="font-mono">{fmtDateTimeIST(historyDc.delivered_at)}</span>
                </div>
              </div>
            )}
            {(historyDc?.amendments || []).length === 0 && <div className="text-sm text-zinc-500 text-center py-8">No amendments recorded.</div>}
            {(historyDc?.amendments || []).slice().reverse().map((a) => (
              <div key={a.amendment_no || a.date} className="p-3 rounded-sm bg-[var(--bg-2)] border border-[var(--border)]" data-testid={`dc-amendment-${a.amendment_no}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-semibold text-amber-400">{a.amendment_no}</span>
                  <span className="text-[10px] font-mono text-zinc-500">{fmtDateTimeIST(a.date)}</span>
                </div>
                <div className="text-xs text-zinc-400 mb-1"><span className="text-zinc-500">By:</span> {a.by}</div>
                <div className="text-sm mb-2"><span className="text-zinc-500 text-xs">Reason:</span> {a.reason}</div>
                {a.changed_fields?.length > 0 && (
                  <div className="text-xs">
                    <div className="text-zinc-500 mb-1">Changed fields:</div>
                    <div className="flex flex-wrap gap-1">
                      {a.changed_fields.map(f => (
                        <span key={f} className="px-1.5 py-0.5 rounded-sm bg-[var(--bg-0)] font-mono text-[10px] text-zinc-300">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setHistoryDc(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
